import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Search,
  ChevronLeft,
  BookOpen,
  Star,
  StarOff,
  Clock,
  CheckCircle,
  XCircle,
  Send,
  Loader2,
  Sparkles,
  Calculator,
  TrendingUp,
  BarChart3,
  ArrowRight,
  RefreshCw,
  MessageCircle,
  Lightbulb,
  GraduationCap,
  Trophy,
  Target,
  Zap,
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== COMPREHENSIVE TERMS DATABASE (160+ TERMS) ====================
const TERMS_DATABASE = [
  // === BASIC FUNDAMENTALS (Must-Know) ===
  {
    id: "stock",
    term: "Stock",
    category: "Basics",
    definition: "A stock represents ownership in a company. When you buy a stock, you become a shareholder and own a small piece of that business. Stock prices fluctuate based on supply and demand, company performance, and market conditions.",
    formula: "Stock Value = Company Value ÷ Total Shares Outstanding",
    formulaVariables: [
      { variable: "Company Value", explanation: "Total worth of the business" },
      { variable: "Shares Outstanding", explanation: "Number of shares available" }
    ],
    example: {
      company: "Apple Inc. (AAPL)",
      data: "If Apple is worth $3 trillion with 15B shares",
      calculation: "Each share = $3T ÷ 15B = $200 per share",
      interpretation: "Owning 1 share means you own 1/15-billionth of Apple."
    },
    quiz: {
      question: "What does owning a stock represent?",
      options: ["A loan to the company", "Ownership in the company", "A promise of dividends", "A bond"],
      correctIndex: 1,
      explanation: "Stocks represent partial ownership (equity) in a company."
    },
    relatedTerms: ["Share", "Equity", "Market Cap", "Shareholder"]
  },
  {
    id: "bond",
    term: "Bond",
    category: "Basics",
    definition: "A bond is a loan you make to a government or corporation. In return, they pay you regular interest (coupon) and return your principal at maturity. Bonds are generally safer than stocks but offer lower returns.",
    formula: "Bond Price = Present Value of Future Coupon Payments + Present Value of Face Value",
    formulaVariables: [
      { variable: "Coupon", explanation: "Regular interest payment" },
      { variable: "Face Value", explanation: "Amount repaid at maturity (usually $1,000)" },
      { variable: "Maturity", explanation: "When the bond expires" }
    ],
    example: {
      company: "Government of Canada Bond",
      data: "10-year bond, 3% coupon, $1,000 face value",
      calculation: "Annual payment = $1,000 × 3% = $30/year",
      interpretation: "You receive $30 annually for 10 years, then get $1,000 back."
    },
    quiz: {
      question: "Bonds are considered:",
      options: ["Equity investments", "Debt investments", "Derivatives", "Commodities"],
      correctIndex: 1,
      explanation: "Bonds are debt. You're lending money and earning interest."
    },
    relatedTerms: ["Yield", "Coupon Rate", "Maturity", "Credit Rating"]
  },
  {
    id: "mer",
    term: "MER (Management Expense Ratio)",
    category: "Basics",
    definition: "MER is the annual fee charged by mutual funds and ETFs, expressed as a percentage of your investment. It covers management, administration, and operating costs. Lower MERs mean more of your returns stay in your pocket.",
    formula: "Annual Fee = Investment Amount × MER",
    formulaVariables: [
      { variable: "MER", explanation: "Percentage fee (e.g., 0.20% or 2.0%)" },
      { variable: "Investment", explanation: "Total amount invested" }
    ],
    example: {
      company: "XEQT vs Active Fund",
      data: "XEQT MER: 0.20%, Active Fund MER: 2.0%, Investment: $100,000",
      calculation: "XEQT fee: $200/year, Active fund: $2,000/year",
      interpretation: "You save $1,800/year with XEQT. That's $18,000 over 10 years!"
    },
    quiz: {
      question: "An ETF with 0.10% MER charges how much annually on $50,000?",
      options: ["$5", "$50", "$500", "$5,000"],
      correctIndex: 1,
      explanation: "Annual fee = $50,000 × 0.10% = $50"
    },
    relatedTerms: ["Expense Ratio", "ETF", "Mutual Fund", "TER"]
  },
  {
    id: "dividend",
    term: "Dividend",
    category: "Basics",
    definition: "A dividend is a cash payment companies make to shareholders from their profits. Not all companies pay dividends. Growth companies often reinvest profits instead. Dividends provide passive income and can be reinvested through DRIPs.",
    formula: "Annual Dividend Income = Shares Owned × Dividend Per Share",
    formulaVariables: [
      { variable: "Shares Owned", explanation: "Number of shares you hold" },
      { variable: "Dividend Per Share", explanation: "Amount paid per share (quarterly or annually)" }
    ],
    example: {
      company: "Royal Bank (RY.TO)",
      data: "Own 100 shares, quarterly dividend: $1.38/share",
      calculation: "Quarterly income = 100 × $1.38 = $138, Annual = $552",
      interpretation: "You earn $552/year in passive income from RY dividends."
    },
    quiz: {
      question: "Which type of company typically pays the highest dividends?",
      options: ["Tech startups", "Growth companies", "Mature, established companies", "Penny stocks"],
      correctIndex: 2,
      explanation: "Mature companies with stable cash flows typically pay higher dividends."
    },
    relatedTerms: ["Dividend Yield", "DRIP", "Ex-Dividend Date", "Payout Ratio"]
  },
  {
    id: "dividend-yield",
    term: "Dividend Yield",
    category: "Basics",
    definition: "Dividend yield shows how much a company pays in dividends relative to its stock price, expressed as a percentage. A 4% yield means you earn $4 annually for every $100 invested. Higher yields can signal value or risk.",
    formula: "Dividend Yield = (Annual Dividend ÷ Stock Price) × 100%",
    formulaVariables: [
      { variable: "Annual Dividend", explanation: "Total dividends paid per share yearly" },
      { variable: "Stock Price", explanation: "Current market price" }
    ],
    example: {
      company: "Enbridge (ENB.TO)",
      data: "Annual dividend: $3.66, Stock price: $50",
      calculation: "Yield = ($3.66 ÷ $50) × 100% = 7.32%",
      interpretation: "Enbridge pays 7.32% annually. Very high for a blue-chip stock."
    },
    quiz: {
      question: "A stock at $80 pays $4 annual dividend. What is the yield?",
      options: ["2%", "4%", "5%", "8%"],
      correctIndex: 2,
      explanation: "Yield = $4 ÷ $80 = 5%"
    },
    relatedTerms: ["Dividend", "Yield on Cost", "High-Yield Stocks", "Income Investing"]
  },
  {
    id: "pe-ratio",
    term: "P/E Ratio (Price-to-Earnings)",
    category: "Valuation",
    definition: "The P/E ratio shows how much investors pay for each dollar of company earnings. A P/E of 20 means you pay $20 for $1 of earnings. Lower P/E may indicate undervaluation; higher P/E suggests growth expectations or overvaluation.",
    formula: "P/E Ratio = Stock Price ÷ Earnings Per Share (EPS)",
    formulaVariables: [
      { variable: "Stock Price", explanation: "Current market price" },
      { variable: "EPS", explanation: "Annual earnings divided by shares" }
    ],
    example: {
      company: "Apple (AAPL)",
      data: "Stock: $180, EPS: $6.00",
      calculation: "P/E = $180 ÷ $6 = 30",
      interpretation: "Investors pay $30 for every $1 Apple earns. A premium for quality."
    },
    quiz: {
      question: "Stock price: $100, EPS: $5. What is the P/E ratio?",
      options: ["5", "15", "20", "500"],
      correctIndex: 2,
      explanation: "P/E = $100 ÷ $5 = 20"
    },
    relatedTerms: ["EPS", "Forward P/E", "PEG Ratio", "Valuation"]
  },
  {
    id: "market-cap",
    term: "Market Cap (Market Capitalization)",
    category: "Basics",
    definition: "Market cap is the total value of all a company's shares. It's calculated by multiplying stock price by shares outstanding. Companies are categorized as large-cap (>$10B), mid-cap ($2-10B), or small-cap (<$2B).",
    formula: "Market Cap = Stock Price × Shares Outstanding",
    formulaVariables: [
      { variable: "Stock Price", explanation: "Current trading price" },
      { variable: "Shares Outstanding", explanation: "Total shares available" }
    ],
    example: {
      company: "Shopify (SHOP.TO)",
      data: "Price: $100, Shares: 1.3B",
      calculation: "Market Cap = $100 × 1.3B = $130 Billion",
      interpretation: "Shopify is a large-cap company worth $130B."
    },
    quiz: {
      question: "A company with 500M shares at $40 each has what market cap?",
      options: ["$2 Billion", "$20 Billion", "$200 Billion", "$2 Trillion"],
      correctIndex: 1,
      explanation: "Market Cap = 500M × $40 = $20 Billion"
    },
    relatedTerms: ["Large Cap", "Small Cap", "Enterprise Value", "Float"]
  },
  {
    id: "eps",
    term: "EPS (Earnings Per Share)",
    category: "Fundamental Analysis",
    definition: "EPS shows how much profit a company makes per share. It's net income divided by shares outstanding. Higher EPS indicates greater profitability. EPS growth over time is a key indicator of company health.",
    formula: "EPS = Net Income ÷ Shares Outstanding",
    formulaVariables: [
      { variable: "Net Income", explanation: "Total profit after all expenses" },
      { variable: "Shares Outstanding", explanation: "Number of shares" }
    ],
    example: {
      company: "Microsoft (MSFT)",
      data: "Net Income: $72B, Shares: 7.5B",
      calculation: "EPS = $72B ÷ 7.5B = $9.60",
      interpretation: "Each MSFT share earned $9.60 last year."
    },
    quiz: {
      question: "Net income: $50M, Shares: 10M. What is EPS?",
      options: ["$0.50", "$5", "$50", "$500"],
      correctIndex: 1,
      explanation: "EPS = $50M ÷ 10M = $5 per share"
    },
    relatedTerms: ["P/E Ratio", "Net Income", "Diluted EPS", "Earnings"]
  },
  // === MORE VALUATION METRICS ===
  {
    id: "price-to-book",
    term: "Price-to-Book Ratio (P/B)",
    category: "Valuation",
    definition: "P/B compares stock price to book value (assets minus liabilities). A P/B under 1 means the stock trades below its net asset value, potentially undervalued. Banks and financial companies are often valued using P/B.",
    formula: "P/B = Stock Price ÷ Book Value Per Share",
    formulaVariables: [
      { variable: "Stock Price", explanation: "Current market price" },
      { variable: "Book Value", explanation: "(Assets - Liabilities) ÷ Shares" }
    ],
    example: {
      company: "TD Bank (TD.TO)",
      data: "Stock: $85, Book Value: $70",
      calculation: "P/B = $85 ÷ $70 = 1.21",
      interpretation: "TD trades at 21% premium to book value. Normal for quality banks."
    },
    quiz: {
      question: "A stock at $50 with book value of $25 has P/B of:",
      options: ["0.5", "1.0", "2.0", "25"],
      correctIndex: 2,
      explanation: "P/B = $50 ÷ $25 = 2.0"
    },
    relatedTerms: ["Book Value", "NAV", "Tangible Book Value", "P/E Ratio"]
  },
  {
    id: "price-to-sales",
    term: "Price-to-Sales Ratio (P/S)",
    category: "Valuation",
    definition: "P/S compares market cap to revenue. It's useful for valuing unprofitable growth companies where P/E doesn't work. Lower P/S generally indicates better value. Tech companies often have high P/S ratios.",
    formula: "P/S = Market Cap ÷ Annual Revenue",
    formulaVariables: [
      { variable: "Market Cap", explanation: "Total market value" },
      { variable: "Revenue", explanation: "Annual sales" }
    ],
    example: {
      company: "Shopify (SHOP)",
      data: "Market Cap: $130B, Revenue: $7B",
      calculation: "P/S = $130B ÷ $7B = 18.6",
      interpretation: "Investors pay $18.60 for every $1 of Shopify revenue."
    },
    quiz: {
      question: "Market cap: $10B, Revenue: $2B. P/S ratio?",
      options: ["0.2", "2", "5", "20"],
      correctIndex: 2,
      explanation: "P/S = $10B ÷ $2B = 5"
    },
    relatedTerms: ["Revenue", "Market Cap", "EV/Sales", "Growth Stocks"]
  },
  {
    id: "peg-ratio",
    term: "PEG Ratio",
    category: "Valuation",
    definition: "PEG adjusts P/E for growth rate. A PEG of 1 means fair value; below 1 may be undervalued; above 1 potentially overvalued. It helps compare companies with different growth rates.",
    formula: "PEG = P/E Ratio ÷ Annual Earnings Growth Rate (%)",
    formulaVariables: [
      { variable: "P/E", explanation: "Price-to-earnings ratio" },
      { variable: "Growth Rate", explanation: "Expected EPS growth percentage" }
    ],
    example: {
      company: "NVIDIA (NVDA)",
      data: "P/E: 50, Growth Rate: 40%",
      calculation: "PEG = 50 ÷ 40 = 1.25",
      interpretation: "Slightly expensive relative to growth, but premium for AI leader."
    },
    quiz: {
      question: "P/E of 30, growth rate of 15%. PEG is:",
      options: ["0.5", "1.0", "2.0", "45"],
      correctIndex: 2,
      explanation: "PEG = 30 ÷ 15 = 2.0"
    },
    relatedTerms: ["P/E Ratio", "Growth Rate", "GARP Investing", "Forward P/E"]
  },
  {
    id: "enterprise-value",
    term: "Enterprise Value (EV)",
    category: "Valuation",
    definition: "EV is the total value to acquire a company: market cap plus debt minus cash. It's more comprehensive than market cap because it includes the debt an acquirer would assume and cash they'd receive.",
    formula: "EV = Market Cap + Total Debt - Cash",
    formulaVariables: [
      { variable: "Market Cap", explanation: "Value of all shares" },
      { variable: "Debt", explanation: "All borrowings" },
      { variable: "Cash", explanation: "Cash and equivalents (subtracted)" }
    ],
    example: {
      company: "Rogers Communications (RCI.B.TO)",
      data: "Market Cap: $30B, Debt: $40B, Cash: $2B",
      calculation: "EV = $30B + $40B - $2B = $68B",
      interpretation: "True takeover cost is $68B due to high debt."
    },
    quiz: {
      question: "Market cap: $50B, Debt: $10B, Cash: $5B. EV is:",
      options: ["$45B", "$55B", "$65B", "$75B"],
      correctIndex: 1,
      explanation: "EV = $50B + $10B - $5B = $55B"
    },
    relatedTerms: ["Market Cap", "EV/EBITDA", "Debt", "Acquisition"]
  },
  {
    id: "ev-ebitda",
    term: "EV/EBITDA",
    category: "Valuation",
    definition: "EV/EBITDA compares enterprise value to operating earnings. It's useful for comparing companies with different debt levels. Lower ratios suggest better value. Industry averages vary widely.",
    formula: "EV/EBITDA = Enterprise Value ÷ EBITDA",
    formulaVariables: [
      { variable: "EV", explanation: "Market cap + debt - cash" },
      { variable: "EBITDA", explanation: "Earnings before interest, taxes, depreciation, amortization" }
    ],
    example: {
      company: "Telus (T.TO)",
      data: "EV: $60B, EBITDA: $6B",
      calculation: "EV/EBITDA = $60B ÷ $6B = 10x",
      interpretation: "10x is reasonable for a stable telecom company."
    },
    quiz: {
      question: "EV: $100B, EBITDA: $10B. EV/EBITDA is:",
      options: ["1x", "5x", "10x", "100x"],
      correctIndex: 2,
      explanation: "EV/EBITDA = $100B ÷ $10B = 10x"
    },
    relatedTerms: ["EBITDA", "Enterprise Value", "Valuation Multiple", "Comparable Analysis"]
  },
  // === PROFITABILITY METRICS ===
  {
    id: "gross-margin",
    term: "Gross Margin",
    category: "Fundamental Analysis",
    definition: "Gross margin is the percentage of revenue remaining after subtracting cost of goods sold (COGS). Higher gross margins indicate better pricing power and production efficiency. Software companies often have 80%+ gross margins.",
    formula: "Gross Margin = ((Revenue - COGS) ÷ Revenue) × 100%",
    formulaVariables: [
      { variable: "Revenue", explanation: "Total sales" },
      { variable: "COGS", explanation: "Direct costs to produce goods/services" }
    ],
    example: {
      company: "Lululemon (LULU)",
      data: "Revenue: $9B, COGS: $4B",
      calculation: "Gross Margin = ($9B - $4B) ÷ $9B = 55.6%",
      interpretation: "Lulu keeps 55 cents per dollar before operating expenses."
    },
    quiz: {
      question: "Revenue: $200M, COGS: $120M. Gross margin?",
      options: ["40%", "60%", "120%", "167%"],
      correctIndex: 0,
      explanation: "Gross Margin = ($200M - $120M) ÷ $200M = 40%"
    },
    relatedTerms: ["COGS", "Operating Margin", "Net Margin", "Gross Profit"]
  },
  {
    id: "operating-margin",
    term: "Operating Margin",
    category: "Fundamental Analysis",
    definition: "Operating margin shows what percentage of revenue remains after all operating expenses. It measures core business profitability before interest and taxes. Higher operating margins indicate efficient operations.",
    formula: "Operating Margin = (Operating Income ÷ Revenue) × 100%",
    formulaVariables: [
      { variable: "Operating Income", explanation: "Revenue minus COGS and operating expenses" },
      { variable: "Revenue", explanation: "Total sales" }
    ],
    example: {
      company: "Meta/Facebook (META)",
      data: "Operating Income: $47B, Revenue: $135B",
      calculation: "Operating Margin = $47B ÷ $135B = 34.8%",
      interpretation: "Meta keeps 35 cents per dollar after operating costs."
    },
    quiz: {
      question: "Operating income: $25M, Revenue: $100M. Operating margin?",
      options: ["4%", "25%", "75%", "400%"],
      correctIndex: 1,
      explanation: "Operating Margin = $25M ÷ $100M = 25%"
    },
    relatedTerms: ["Gross Margin", "Net Margin", "EBIT", "Operating Leverage"]
  },
  {
    id: "net-margin",
    term: "Net Profit Margin",
    category: "Fundamental Analysis",
    definition: "Net margin is the percentage of revenue that becomes profit after ALL expenses including taxes and interest. It's the bottom-line profitability metric. Higher net margins indicate efficient, profitable businesses.",
    formula: "Net Margin = (Net Income ÷ Revenue) × 100%",
    formulaVariables: [
      { variable: "Net Income", explanation: "Final profit after everything" },
      { variable: "Revenue", explanation: "Total sales" }
    ],
    example: {
      company: "Visa (V)",
      data: "Net Income: $17B, Revenue: $33B",
      calculation: "Net Margin = $17B ÷ $33B = 51.5%",
      interpretation: "Visa keeps 51 cents profit from every dollar. Exceptional."
    },
    quiz: {
      question: "Net income: $8M, Revenue: $40M. Net margin?",
      options: ["5%", "20%", "32%", "500%"],
      correctIndex: 1,
      explanation: "Net Margin = $8M ÷ $40M = 20%"
    },
    relatedTerms: ["Gross Margin", "Operating Margin", "Net Income", "Profitability"]
  },
  {
    id: "roe",
    term: "ROE (Return on Equity)",
    category: "Fundamental Analysis",
    definition: "ROE measures how efficiently a company generates profits from shareholders' equity. Higher ROE means better capital efficiency. Very high ROE may indicate excessive leverage. 15-20% ROE is typically considered good.",
    formula: "ROE = (Net Income ÷ Shareholders' Equity) × 100%",
    formulaVariables: [
      { variable: "Net Income", explanation: "Annual profit" },
      { variable: "Equity", explanation: "Assets minus liabilities (book value)" }
    ],
    example: {
      company: "Royal Bank (RY.TO)",
      data: "Net Income: $16B, Equity: $100B",
      calculation: "ROE = $16B ÷ $100B = 16%",
      interpretation: "RY generates 16% return on shareholder capital. Excellent for a bank."
    },
    quiz: {
      question: "Net income: $10M, Equity: $50M. ROE is:",
      options: ["5%", "20%", "50%", "500%"],
      correctIndex: 1,
      explanation: "ROE = $10M ÷ $50M = 20%"
    },
    relatedTerms: ["ROA", "ROIC", "Equity", "DuPont Analysis"]
  },
  {
    id: "roa",
    term: "ROA (Return on Assets)",
    category: "Fundamental Analysis",
    definition: "ROA measures how efficiently a company uses its total assets to generate profit. It shows profitability relative to all resources, not just equity. Banks typically have low ROA (1-2%) due to high assets.",
    formula: "ROA = (Net Income ÷ Total Assets) × 100%",
    formulaVariables: [
      { variable: "Net Income", explanation: "Annual profit" },
      { variable: "Total Assets", explanation: "Everything the company owns" }
    ],
    example: {
      company: "Apple (AAPL)",
      data: "Net Income: $97B, Assets: $352B",
      calculation: "ROA = $97B ÷ $352B = 27.6%",
      interpretation: "Apple earns 27.6% on its assets. Remarkably efficient."
    },
    quiz: {
      question: "Net income: $5M, Total assets: $100M. ROA is:",
      options: ["0.5%", "5%", "20%", "50%"],
      correctIndex: 1,
      explanation: "ROA = $5M ÷ $100M = 5%"
    },
    relatedTerms: ["ROE", "Asset Turnover", "Net Income", "Efficiency"]
  },
  // === FINANCIAL HEALTH ===
  {
    id: "debt-to-equity",
    term: "Debt-to-Equity Ratio (D/E)",
    category: "Fundamental Analysis",
    definition: "D/E compares total debt to shareholders' equity, showing how much debt finances operations. Higher ratios mean more leverage and risk. D/E under 1 is conservative; over 2 is aggressive. Varies by industry.",
    formula: "D/E = Total Debt ÷ Shareholders' Equity",
    formulaVariables: [
      { variable: "Total Debt", explanation: "All borrowings (short + long term)" },
      { variable: "Equity", explanation: "Book value (assets - liabilities)" }
    ],
    example: {
      company: "BCE Inc. (BCE.TO)",
      data: "Debt: $40B, Equity: $20B",
      calculation: "D/E = $40B ÷ $20B = 2.0",
      interpretation: "BCE uses $2 of debt for every $1 of equity. High leverage for stable cash flows."
    },
    quiz: {
      question: "Debt: $30M, Equity: $60M. D/E ratio is:",
      options: ["0.25", "0.50", "2.0", "90"],
      correctIndex: 1,
      explanation: "D/E = $30M ÷ $60M = 0.50"
    },
    relatedTerms: ["Leverage", "Interest Coverage", "Debt Ratio", "Financial Risk"]
  },
  {
    id: "current-ratio",
    term: "Current Ratio",
    category: "Fundamental Analysis",
    definition: "Current ratio measures ability to pay short-term debts with short-term assets. Above 1 means the company can cover current liabilities; below 1 signals potential liquidity issues. 1.5-2.0 is typically healthy.",
    formula: "Current Ratio = Current Assets ÷ Current Liabilities",
    formulaVariables: [
      { variable: "Current Assets", explanation: "Cash, inventory, receivables (due within 1 year)" },
      { variable: "Current Liabilities", explanation: "Debts due within 1 year" }
    ],
    example: {
      company: "Canadian Tire (CTC.A)",
      data: "Current Assets: $8B, Current Liabilities: $5B",
      calculation: "Current Ratio = $8B ÷ $5B = 1.6",
      interpretation: "Canadian Tire can comfortably cover short-term obligations."
    },
    quiz: {
      question: "Current assets: $100M, Current liabilities: $50M. Current ratio?",
      options: ["0.5", "1.0", "2.0", "50"],
      correctIndex: 2,
      explanation: "Current Ratio = $100M ÷ $50M = 2.0"
    },
    relatedTerms: ["Quick Ratio", "Working Capital", "Liquidity", "Cash Ratio"]
  },
  {
    id: "quick-ratio",
    term: "Quick Ratio (Acid Test)",
    category: "Fundamental Analysis",
    definition: "Quick ratio is a stricter liquidity test that excludes inventory (which may be hard to sell quickly). It shows ability to meet obligations with the most liquid assets. 1.0+ is generally considered healthy.",
    formula: "Quick Ratio = (Current Assets - Inventory) ÷ Current Liabilities",
    formulaVariables: [
      { variable: "Current Assets", explanation: "Short-term assets" },
      { variable: "Inventory", explanation: "Excluded because less liquid" },
      { variable: "Current Liabilities", explanation: "Short-term debts" }
    ],
    example: {
      company: "Dollarama (DOL.TO)",
      data: "Current Assets: $1.5B, Inventory: $700M, Current Liabilities: $600M",
      calculation: "Quick Ratio = ($1.5B - $700M) ÷ $600M = 1.33",
      interpretation: "Even without selling inventory, Dollarama can pay its bills."
    },
    quiz: {
      question: "Current assets: $80M, Inventory: $30M, Current liabilities: $25M. Quick ratio?",
      options: ["1.0", "2.0", "3.2", "5.0"],
      correctIndex: 1,
      explanation: "Quick Ratio = ($80M - $30M) ÷ $25M = 2.0"
    },
    relatedTerms: ["Current Ratio", "Liquidity", "Working Capital", "Cash Ratio"]
  },
  {
    id: "free-cash-flow",
    term: "Free Cash Flow (FCF)",
    category: "Fundamental Analysis",
    definition: "FCF is cash remaining after capital expenditures. It's money available for dividends, buybacks, debt repayment, or acquisitions. Positive FCF indicates financial health; negative FCF may signal growth investment or trouble.",
    formula: "FCF = Operating Cash Flow - Capital Expenditures",
    formulaVariables: [
      { variable: "Operating Cash Flow", explanation: "Cash from business operations" },
      { variable: "CapEx", explanation: "Spending on property, plant, equipment" }
    ],
    example: {
      company: "Google/Alphabet (GOOGL)",
      data: "Operating CF: $100B, CapEx: $32B",
      calculation: "FCF = $100B - $32B = $68 Billion",
      interpretation: "Google generates $68B in free cash annually. Enormous flexibility."
    },
    quiz: {
      question: "Operating cash flow: $50M, CapEx: $20M. FCF is:",
      options: ["$20M", "$30M", "$50M", "$70M"],
      correctIndex: 1,
      explanation: "FCF = $50M - $20M = $30M"
    },
    relatedTerms: ["Cash Flow", "CapEx", "Operating Cash Flow", "EBITDA"]
  },
  {
    id: "ebitda",
    term: "EBITDA",
    category: "Fundamental Analysis",
    definition: "EBITDA = Earnings Before Interest, Taxes, Depreciation, and Amortization. It measures operating performance by removing financing and accounting decisions. Useful for comparing companies across industries.",
    formula: "EBITDA = Net Income + Interest + Taxes + Depreciation + Amortization",
    formulaVariables: [
      { variable: "Net Income", explanation: "Bottom-line profit" },
      { variable: "Interest", explanation: "Borrowing costs" },
      { variable: "D&A", explanation: "Non-cash expenses" }
    ],
    example: {
      company: "Rogers (RCI.B.TO)",
      data: "Net Income: $1.5B, Interest: $1B, Taxes: $0.5B, D&A: $3B",
      calculation: "EBITDA = $1.5B + $1B + $0.5B + $3B = $6B",
      interpretation: "Rogers generates $6B in EBITDA before financing costs."
    },
    quiz: {
      question: "EBITDA is useful because it:",
      options: ["Includes all expenses", "Removes financing and accounting effects", "Only measures cash", "Predicts stock price"],
      correctIndex: 1,
      explanation: "EBITDA isolates operating performance from capital structure and accounting."
    },
    relatedTerms: ["EBIT", "Operating Income", "EV/EBITDA", "Cash Flow"]
  },
  // === TECHNICAL ANALYSIS ===
  {
    id: "support-resistance",
    term: "Support & Resistance",
    category: "Technical Analysis",
    definition: "Support is a price floor where buying interest prevents further decline. Resistance is a ceiling where selling pressure halts advances. These levels help identify entry/exit points and stop-loss placement.",
    formula: "Identified through chart patterns, previous highs/lows, and volume",
    formulaVariables: [
      { variable: "Support", explanation: "Price level where buyers step in" },
      { variable: "Resistance", explanation: "Price level where sellers emerge" }
    ],
    example: {
      company: "S&P 500",
      data: "Support: 4,800, Resistance: 5,000",
      calculation: "Price bounces between these levels",
      interpretation: "Break above 5,000 could signal continuation; break below 4,800 suggests weakness."
    },
    quiz: {
      question: "When price breaks above resistance, it often:",
      options: ["Becomes new support", "Falls sharply", "Stays unchanged", "Doubles"],
      correctIndex: 0,
      explanation: "Old resistance often becomes new support after a breakout."
    },
    relatedTerms: ["Breakout", "Trendline", "Moving Average", "Volume"]
  },
  {
    id: "moving-average",
    term: "Moving Average (MA)",
    category: "Technical Analysis",
    definition: "A moving average smooths price data over a specified period. The 50-day and 200-day MAs are most popular. Price above MAs suggests uptrend; below suggests downtrend. MA crossovers signal potential trend changes.",
    formula: "Simple MA = Sum of Closing Prices ÷ Number of Periods",
    formulaVariables: [
      { variable: "Closing Prices", explanation: "End-of-day prices" },
      { variable: "Periods", explanation: "Number of days (e.g., 50, 200)" }
    ],
    example: {
      company: "XEQT.TO",
      data: "50-day MA: $28.50, 200-day MA: $27.00, Current: $29.00",
      calculation: "Price above both MAs = bullish",
      interpretation: "XEQT is in an uptrend, trading above key moving averages."
    },
    quiz: {
      question: "A 'death cross' occurs when:",
      options: ["50-day MA crosses above 200-day", "50-day MA crosses below 200-day", "Price hits zero", "Volume spikes"],
      correctIndex: 1,
      explanation: "Death cross (50-day below 200-day) is a bearish signal."
    },
    relatedTerms: ["Golden Cross", "Death Cross", "EMA", "Trend"]
  },
  {
    id: "rsi",
    term: "RSI (Relative Strength Index)",
    category: "Technical Analysis",
    definition: "RSI measures momentum on a 0-100 scale. Above 70 indicates overbought (potential sell); below 30 indicates oversold (potential buy). It helps identify potential reversals and entry/exit points.",
    formula: "RSI = 100 - (100 ÷ (1 + RS)), where RS = Avg Gain ÷ Avg Loss",
    formulaVariables: [
      { variable: "RS", explanation: "Relative Strength (gains vs losses)" },
      { variable: "Period", explanation: "Typically 14 days" }
    ],
    example: {
      company: "Tesla (TSLA)",
      data: "RSI: 78 after strong rally",
      calculation: "RSI > 70 = overbought",
      interpretation: "Tesla may be due for a pullback after aggressive buying."
    },
    quiz: {
      question: "An RSI of 25 indicates:",
      options: ["Overbought", "Oversold", "Neutral", "Trending"],
      correctIndex: 1,
      explanation: "RSI below 30 suggests oversold conditions. A potential buying opportunity."
    },
    relatedTerms: ["MACD", "Overbought", "Oversold", "Momentum"]
  },
  {
    id: "volume",
    term: "Trading Volume",
    category: "Technical Analysis",
    definition: "Volume is the number of shares traded in a period. High volume confirms price moves; low volume suggests weak conviction. Volume spikes often precede or accompany significant price changes.",
    formula: "Volume = Total Shares Traded in Period",
    formulaVariables: [
      { variable: "Shares Traded", explanation: "Number of shares changing hands" },
      { variable: "Period", explanation: "Day, week, minute, etc." }
    ],
    example: {
      company: "GameStop (GME)",
      data: "Normal: 5M/day, Squeeze: 197M/day",
      calculation: "40x normal volume",
      interpretation: "Massive volume confirmed extreme retail buying interest."
    },
    quiz: {
      question: "A price breakout on low volume suggests:",
      options: ["Strong trend", "Weak/false breakout", "Buy signal", "Dividend coming"],
      correctIndex: 1,
      explanation: "Breakouts need high volume for confirmation."
    },
    relatedTerms: ["Liquidity", "VWAP", "Volume Profile", "Confirmation"]
  },
  {
    id: "beta",
    term: "Beta",
    category: "Technical Analysis",
    definition: "Beta measures a stock's volatility relative to the market. Beta of 1 = moves with market; >1 = more volatile; <1 = less volatile; negative = moves opposite. Used in portfolio risk management.",
    formula: "Beta = Covariance(Stock, Market) ÷ Variance(Market)",
    formulaVariables: [
      { variable: "Covariance", explanation: "How stock and market move together" },
      { variable: "Variance", explanation: "Market's price variability" }
    ],
    example: {
      company: "Utilities (XLU) vs Tech (QQQ)",
      data: "XLU Beta: 0.5, QQQ Beta: 1.2",
      calculation: "If market drops 10%: XLU drops ~5%, QQQ drops ~12%",
      interpretation: "Utilities are defensive; tech is aggressive."
    },
    quiz: {
      question: "A stock with beta 1.5 when market rises 10% should rise:",
      options: ["5%", "10%", "15%", "50%"],
      correctIndex: 2,
      explanation: "Expected move = Beta × Market Move = 1.5 × 10% = 15%"
    },
    relatedTerms: ["Alpha", "Volatility", "CAPM", "Risk"]
  },
  // === CANADIAN-SPECIFIC ===
  {
    id: "tfsa",
    term: "TFSA (Tax-Free Savings Account)",
    category: "Canadian Accounts",
    definition: "A TFSA lets Canadians invest with completely tax-free growth and withdrawals. Contributions aren't tax-deductible, but all gains (dividends, interest, capital gains) are never taxed. Perfect for long-term growth investing.",
    formula: "2024 Limit: $7,000/year; Lifetime (since 2009): $95,000",
    formulaVariables: [
      { variable: "Contribution Room", explanation: "Annual limit plus unused prior years" },
      { variable: "Withdrawals", explanation: "Added back to next year's room" }
    ],
    example: {
      company: "TFSA Strategy",
      data: "Max contributions since 2009: $95,000, 7% annual return for 20 years",
      calculation: "$95K grows to ~$368K, all tax-free",
      interpretation: "$273K in tax-free gains!"
    },
    quiz: {
      question: "TFSA withdrawals are taxed at:",
      options: ["Capital gains rate", "Marginal income rate", "15% flat rate", "Never taxed"],
      correctIndex: 3,
      explanation: "TFSA withdrawals are 100% tax-free."
    },
    relatedTerms: ["RRSP", "FHSA", "Tax-Free Growth", "Contribution Room"]
  },
  {
    id: "rrsp",
    term: "RRSP (Registered Retirement Savings Plan)",
    category: "Canadian Accounts",
    definition: "An RRSP provides tax-deferred retirement savings. Contributions reduce your taxable income now; investments grow tax-free; withdrawals are taxed as income in retirement when you're likely in a lower tax bracket.",
    formula: "RRSP Limit = 18% of prior year income (max ~$31,560 for 2024)",
    formulaVariables: [
      { variable: "Tax Deduction", explanation: "Reduces current taxable income" },
      { variable: "Tax Deferral", explanation: "Pay tax on withdrawals later" }
    ],
    example: {
      company: "RRSP Tax Benefit",
      data: "Income: $100K (43% bracket), Contribute $20K",
      calculation: "Tax savings = $20K × 43% = $8,600 refund now",
      interpretation: "Government gives you $8,600 to invest for retirement."
    },
    quiz: {
      question: "RRSP contributions:",
      options: ["Are taxed now", "Reduce taxable income", "Are tax-free forever", "Require matching"],
      correctIndex: 1,
      explanation: "RRSP contributions lower your current taxable income."
    },
    relatedTerms: ["TFSA", "RRIF", "Tax Deferral", "Retirement"]
  },
  {
    id: "fhsa",
    term: "FHSA (First Home Savings Account)",
    category: "Canadian Accounts",
    definition: "FHSA combines RRSP and TFSA benefits for first-time home buyers. Contributions are tax-deductible (like RRSP), and withdrawals for home purchase are tax-free (like TFSA). Best of both worlds!",
    formula: "Annual Limit: $8,000; Lifetime Max: $40,000",
    formulaVariables: [
      { variable: "Tax Deduction", explanation: "Reduces current income" },
      { variable: "Tax-Free Withdrawal", explanation: "For qualifying home purchase" }
    ],
    example: {
      company: "FHSA Strategy",
      data: "Max $40K over 5 years at 30% tax bracket",
      calculation: "Tax savings: $12K + Tax-free growth + Tax-free withdrawal",
      interpretation: "Triple tax advantage for home buyers."
    },
    quiz: {
      question: "FHSA is best described as:",
      options: ["Only for retirement", "RRSP benefits + TFSA benefits", "Only for investments", "Emergency fund"],
      correctIndex: 1,
      explanation: "FHSA gives RRSP's deduction AND TFSA's tax-free withdrawal for home buying."
    },
    relatedTerms: ["TFSA", "RRSP", "First-Time Home Buyer", "Down Payment"]
  },
  {
    id: "resp",
    term: "RESP (Registered Education Savings Plan)",
    category: "Canadian Accounts",
    definition: "RESP is a tax-sheltered account for children's education. Government adds 20% through CESG (up to $500/year). Growth is tax-free; withdrawals are taxed in the student's hands at their low rate.",
    formula: "CESG = 20% of contribution (max $500/year, $7,200 lifetime)",
    formulaVariables: [
      { variable: "CESG", explanation: "Canada Education Savings Grant (free money!)" },
      { variable: "Lifetime Limit", explanation: "$50,000 per beneficiary" }
    ],
    example: {
      company: "RESP Strategy",
      data: "Contribute $2,500/year for 18 years",
      calculation: "You: $45K, CESG: $7.2K, Growth at 7%: ~$100K+ total",
      interpretation: "Free government money + tax-free growth = educated kids."
    },
    quiz: {
      question: "The CESG provides what % matching on RESP contributions?",
      options: ["10%", "20%", "50%", "100%"],
      correctIndex: 1,
      explanation: "CESG matches 20% of contributions up to $500/year."
    },
    relatedTerms: ["CESG", "Education Savings", "Tax-Free Growth", "TFSA"]
  },
  // === OPTIONS ===
  {
    id: "call-option",
    term: "Call Option",
    category: "Options",
    definition: "A call gives you the right (not obligation) to BUY a stock at a specific price (strike) before expiration. You profit when the stock rises above the strike. Calls provide leveraged upside with limited downside (premium paid).",
    formula: "Call Profit = (Stock Price - Strike) - Premium Paid",
    formulaVariables: [
      { variable: "Strike Price", explanation: "Price you can buy at" },
      { variable: "Premium", explanation: "Cost to buy the option" }
    ],
    example: {
      company: "AAPL $200 Call",
      data: "Premium: $5, Strike: $200, Stock at expiry: $220",
      calculation: "Profit = ($220 - $200) - $5 = $15 per share",
      interpretation: "300% return on the $5 premium!"
    },
    quiz: {
      question: "You buy a $50 call for $3. Stock expires at $60. Profit?",
      options: ["$3", "$7", "$10", "$60"],
      correctIndex: 1,
      explanation: "Profit = ($60 - $50) - $3 = $7 per share"
    },
    relatedTerms: ["Put Option", "Strike Price", "Premium", "In the Money"]
  },
  {
    id: "put-option",
    term: "Put Option",
    category: "Options",
    definition: "A put gives you the right to SELL a stock at a specific price (strike) before expiration. You profit when the stock falls below the strike. Puts are used for downside protection (hedging) or bearish bets.",
    formula: "Put Profit = (Strike - Stock Price) - Premium Paid",
    formulaVariables: [
      { variable: "Strike Price", explanation: "Price you can sell at" },
      { variable: "Premium", explanation: "Cost to buy the option" }
    ],
    example: {
      company: "SPY $450 Put",
      data: "Premium: $10, Strike: $450, SPY at expiry: $400",
      calculation: "Profit = ($450 - $400) - $10 = $40 per share",
      interpretation: "Put protected against a 50-point drop."
    },
    quiz: {
      question: "You buy a $100 put for $5. Stock expires at $80. Profit?",
      options: ["$5", "$15", "$20", "$80"],
      correctIndex: 1,
      explanation: "Profit = ($100 - $80) - $5 = $15 per share"
    },
    relatedTerms: ["Call Option", "Protective Put", "Hedge", "Strike Price"]
  },
  {
    id: "covered-call",
    term: "Covered Call",
    category: "Options",
    definition: "A covered call involves owning stock and selling call options against it. You collect premium income but cap your upside at the strike price. Popular income strategy for dividend investors.",
    formula: "Income = Premium Received; Max Profit = (Strike - Cost) + Premium",
    formulaVariables: [
      { variable: "Premium", explanation: "Income from selling the call" },
      { variable: "Strike", explanation: "Price at which shares may be called away" }
    ],
    example: {
      company: "BMO Covered Call ETF (ZWB)",
      data: "Sells calls on Canadian banks, 6%+ yield",
      calculation: "Premium income + dividends = higher yield",
      interpretation: "Trade upside potential for consistent income."
    },
    quiz: {
      question: "In a covered call, you:",
      options: ["Buy calls on stocks you own", "Sell calls on stocks you own", "Buy puts for protection", "Short stocks"],
      correctIndex: 1,
      explanation: "Covered call = own stock + sell (write) calls to generate income."
    },
    relatedTerms: ["Call Option", "Premium", "Income Strategy", "Assignment"]
  },
  // === MARKET CONDITIONS ===
  {
    id: "bull-market",
    term: "Bull Market",
    category: "Market Conditions",
    definition: "A bull market is a sustained period of rising prices, typically 20%+ from recent lows. Characterized by optimism, economic growth, and investor confidence. The 2009-2020 bull lasted 11 years.",
    formula: "Bull Market = 20%+ rise from bear market low",
    formulaVariables: [
      { variable: "Duration", explanation: "Months to years" },
      { variable: "Catalyst", explanation: "Growth, low rates, innovation" }
    ],
    example: {
      company: "2023-2024 Bull Market",
      data: "S&P 500 rose from 3,577 (Oct 2022) to 5,000+ (2024)",
      calculation: "40%+ gain in ~16 months",
      interpretation: "AI enthusiasm and cooling inflation drove the rally."
    },
    quiz: {
      question: "A bull market is defined by:",
      options: ["20% decline", "20%+ gain", "Recession", "High unemployment"],
      correctIndex: 1,
      explanation: "Bull market = 20%+ rise from recent lows."
    },
    relatedTerms: ["Bear Market", "Correction", "Rally", "All-Time High"]
  },
  {
    id: "bear-market",
    term: "Bear Market",
    category: "Market Conditions",
    definition: "A bear market is a 20%+ decline from recent highs, marked by pessimism, fear, and often recession. Bear markets typically last 9-18 months. They create buying opportunities for long-term investors.",
    formula: "Bear Market = 20%+ decline from recent highs",
    formulaVariables: [
      { variable: "Duration", explanation: "Typically 9-18 months" },
      { variable: "Catalyst", explanation: "Recession, rate hikes, crisis" }
    ],
    example: {
      company: "2022 Bear Market",
      data: "S&P dropped from 4,796 to 3,577 (25% decline)",
      calculation: "Triggered by inflation and rate hikes",
      interpretation: "Fed raised rates from 0% to 5.5% to fight inflation."
    },
    quiz: {
      question: "Bear markets typically last:",
      options: ["1 week", "9-18 months", "5+ years", "Forever"],
      correctIndex: 1,
      explanation: "Average bear market lasts 9-18 months."
    },
    relatedTerms: ["Bull Market", "Correction", "Recession", "Capitulation"]
  },
  {
    id: "correction",
    term: "Market Correction",
    category: "Market Conditions",
    definition: "A correction is a 10-20% decline from recent highs. Corrections are normal and healthy, occurring about once per year on average. They're less severe than bear markets and often present buying opportunities.",
    formula: "Correction = 10-20% decline from recent high",
    formulaVariables: [
      { variable: "Threshold", explanation: "10% minimum, 20% max (then bear)" },
      { variable: "Frequency", explanation: "About once per year" }
    ],
    example: {
      company: "Typical Correction",
      data: "Market at 5,000, drops to 4,500 (10% decline)",
      calculation: "Correction territory",
      interpretation: "Normal volatility. Not a bear market."
    },
    quiz: {
      question: "A 15% decline is called a:",
      options: ["Crash", "Bear market", "Correction", "Recession"],
      correctIndex: 2,
      explanation: "10-20% decline = correction; over 20% = bear market."
    },
    relatedTerms: ["Bear Market", "Pullback", "Buying Opportunity", "Volatility"]
  },
  {
    id: "volatility",
    term: "Volatility",
    category: "Market Conditions",
    definition: "Volatility measures how much prices fluctuate. High volatility = big swings; low volatility = stability. The VIX ('fear index') measures expected S&P 500 volatility. Volatility creates both risk and opportunity.",
    formula: "Volatility = Standard Deviation of Returns",
    formulaVariables: [
      { variable: "VIX", explanation: "12-15 = calm, 20-25 = elevated, 30+ = fearful" },
      { variable: "Std Dev", explanation: "Statistical measure of price variation" }
    ],
    example: {
      company: "VIX Levels",
      data: "Normal: 15, March 2020: 82, Oct 2022: 33",
      calculation: "Higher VIX = more fear",
      interpretation: "VIX spikes often mark bottoms. A contrarian signal."
    },
    quiz: {
      question: "The VIX is called the:",
      options: ["Greed index", "Fear index", "Growth index", "Value index"],
      correctIndex: 1,
      explanation: "VIX rises when investors buy protection, indicating fear."
    },
    relatedTerms: ["VIX", "Standard Deviation", "Beta", "Risk"]
  },
  // === ECONOMIC INDICATORS ===
  {
    id: "inflation",
    term: "Inflation",
    category: "Economics",
    definition: "Inflation is the rate at which prices rise, eroding purchasing power. Central banks target 2% as healthy. High inflation (>3%) prompts rate hikes; deflation (negative) is equally concerning.",
    formula: "Inflation Rate = ((Current CPI - Prior CPI) ÷ Prior CPI) × 100%",
    formulaVariables: [
      { variable: "CPI", explanation: "Consumer Price Index (basket of goods)" },
      { variable: "Core Inflation", explanation: "Excludes volatile food/energy" }
    ],
    example: {
      company: "2022 Inflation Spike",
      data: "US CPI peaked at 9.1% in June 2022",
      calculation: "Highest in 40 years",
      interpretation: "Fed raised rates aggressively to combat inflation."
    },
    quiz: {
      question: "Central banks typically target inflation of:",
      options: ["0%", "2%", "5%", "10%"],
      correctIndex: 1,
      explanation: "Most central banks target ~2% inflation as optimal."
    },
    relatedTerms: ["CPI", "Interest Rates", "Fed", "Deflation"]
  },
  {
    id: "interest-rates",
    term: "Interest Rates",
    category: "Economics",
    definition: "Interest rates are the cost of borrowing money. Central banks set benchmark rates to control inflation and growth. Higher rates slow the economy and hurt stocks; lower rates stimulate growth.",
    formula: "Impact on stocks: Higher rates = lower stock valuations (generally)",
    formulaVariables: [
      { variable: "Fed Funds Rate", explanation: "US benchmark rate" },
      { variable: "Bank of Canada Rate", explanation: "Canadian overnight rate" }
    ],
    example: {
      company: "2022-2023 Rate Hikes",
      data: "Fed: 0% → 5.5%, BoC: 0.25% → 5%",
      calculation: "Fastest hikes in 40 years",
      interpretation: "Aggressive tightening caused 2022 stock/bond selloff."
    },
    quiz: {
      question: "Rising interest rates typically cause stocks to:",
      options: ["Rise", "Fall", "Stay flat", "Double"],
      correctIndex: 1,
      explanation: "Higher rates increase borrowing costs and make bonds more attractive."
    },
    relatedTerms: ["Federal Reserve", "Bank of Canada", "Inflation", "Bonds"]
  },
  {
    id: "gdp",
    term: "GDP (Gross Domestic Product)",
    category: "Economics",
    definition: "GDP measures total value of goods and services produced in a country. It's the primary indicator of economic health. Two consecutive quarters of negative GDP defines a recession.",
    formula: "GDP = Consumption + Investment + Government + Net Exports",
    formulaVariables: [
      { variable: "C", explanation: "Consumer spending (~70% of US GDP)" },
      { variable: "I", explanation: "Business investment" },
      { variable: "G", explanation: "Government spending" },
      { variable: "NX", explanation: "Exports minus imports" }
    ],
    example: {
      company: "Canada GDP 2023",
      data: "GDP: ~$2.1 Trillion CAD",
      calculation: "Growth: ~1.5% real growth",
      interpretation: "Modest growth amid rate hikes."
    },
    quiz: {
      question: "A recession is technically:",
      options: ["Stock market crash", "Two quarters negative GDP", "High unemployment", "Inflation over 5%"],
      correctIndex: 1,
      explanation: "Technical recession = two consecutive quarters of GDP decline."
    },
    relatedTerms: ["Recession", "Economic Growth", "Inflation", "Employment"]
  },
  // === PORTFOLIO MANAGEMENT ===
  {
    id: "diversification",
    term: "Diversification",
    category: "Portfolio Management",
    definition: "'Don't put all eggs in one basket.' Diversification spreads risk across different assets, sectors, and geographies. It reduces portfolio volatility without necessarily sacrificing returns. Key to long-term investing success.",
    formula: "Risk Reduction = Combining uncorrelated or negatively correlated assets",
    formulaVariables: [
      { variable: "Correlation", explanation: "How assets move together (-1 to +1)" },
      { variable: "Asset Classes", explanation: "Stocks, bonds, real estate, etc." }
    ],
    example: {
      company: "All-in-One ETF (XEQT)",
      data: "10,000+ stocks across 40+ countries",
      calculation: "Global diversification in one fund",
      interpretation: "One-ticket solution for complete diversification."
    },
    quiz: {
      question: "True diversification requires assets with:",
      options: ["High correlation", "Low or negative correlation", "Same sector", "Equal prices"],
      correctIndex: 1,
      explanation: "Low correlation means assets don't move together, reducing risk."
    },
    relatedTerms: ["Asset Allocation", "Correlation", "Risk Management", "Portfolio"]
  },
  {
    id: "asset-allocation",
    term: "Asset Allocation",
    category: "Portfolio Management",
    definition: "Asset allocation is dividing your portfolio among asset classes (stocks, bonds, cash). It's the primary determinant of long-term returns and risk. Your allocation should match your goals, timeline, and risk tolerance.",
    formula: "Rule of thumb: Stock % = 110 - Age (aggressive) or 100 - Age (moderate)",
    formulaVariables: [
      { variable: "Stocks", explanation: "Growth, higher risk" },
      { variable: "Bonds", explanation: "Stability, lower returns" },
      { variable: "Cash", explanation: "Safety, near-zero returns" }
    ],
    example: {
      company: "30-Year-Old Investor",
      data: "Long time horizon, high risk tolerance",
      calculation: "110 - 30 = 80% stocks, 20% bonds",
      interpretation: "XEQT (100% stocks) or XGRO (80/20) appropriate."
    },
    quiz: {
      question: "Asset allocation determines approximately what % of returns?",
      options: ["10%", "50%", "90%", "100%"],
      correctIndex: 2,
      explanation: "Studies show asset allocation drives ~90% of portfolio return variability."
    },
    relatedTerms: ["Diversification", "Rebalancing", "Risk Tolerance", "Time Horizon"]
  },
  {
    id: "rebalancing",
    term: "Portfolio Rebalancing",
    category: "Portfolio Management",
    definition: "Rebalancing means periodically adjusting your portfolio back to target allocations. As some assets outperform, they become overweighted. Rebalancing maintains your intended risk level by selling winners and buying laggards.",
    formula: "Rebalance when allocation drifts >5% from target",
    formulaVariables: [
      { variable: "Target", explanation: "Your intended allocation" },
      { variable: "Drift", explanation: "Deviation due to market moves" }
    ],
    example: {
      company: "80/20 Portfolio",
      data: "After rally: 90% stocks, 10% bonds",
      calculation: "Sell 10% stocks, buy 10% bonds",
      interpretation: "Forces discipline: buy low, sell high."
    },
    quiz: {
      question: "Rebalancing primarily helps with:",
      options: ["Maximizing returns", "Maintaining target risk", "Avoiding taxes", "Market timing"],
      correctIndex: 1,
      explanation: "Rebalancing maintains your intended risk exposure."
    },
    relatedTerms: ["Asset Allocation", "Risk Management", "Buy Low Sell High"]
  },
  {
    id: "drip",
    term: "DRIP (Dividend Reinvestment Plan)",
    category: "Portfolio Management",
    definition: "A DRIP automatically reinvests dividends to buy more shares, often commission-free. This compounds growth over time. Many Canadian brokerages offer synthetic DRIPs that reinvest dividends automatically.",
    formula: "Compound Growth = Dividends reinvested → More shares → More dividends",
    formulaVariables: [
      { variable: "Automatic", explanation: "Dividends buy more shares" },
      { variable: "Compounding", explanation: "Exponential growth over time" }
    ],
    example: {
      company: "DRIP on TD Bank (TD.TO)",
      data: "$10,000 invested, 4% yield, reinvested for 30 years",
      calculation: "Without DRIP: ~$32K; With DRIP: ~$56K",
      interpretation: "DRIP nearly doubles long-term returns through compounding."
    },
    quiz: {
      question: "DRIP stands for:",
      options: ["Direct Return Investment Program", "Dividend Reinvestment Plan", "Daily Rate Interest Payment", "Debt Reduction Investment Plan"],
      correctIndex: 1,
      explanation: "DRIP = Dividend Reinvestment Plan"
    },
    relatedTerms: ["Dividend", "Compounding", "Automatic Investing", "Growth"]
  },
  {
    id: "dollar-cost-averaging",
    term: "Dollar-Cost Averaging (DCA)",
    category: "Portfolio Management",
    definition: "DCA means investing a fixed amount regularly regardless of price. You buy more shares when prices are low, fewer when high. This reduces timing risk and emotional decision-making. Perfect for automatic investing.",
    formula: "Average Cost = Total Invested ÷ Total Shares Purchased",
    formulaVariables: [
      { variable: "Fixed Amount", explanation: "Same investment each period" },
      { variable: "Timing Risk", explanation: "Reduced by spreading purchases" }
    ],
    example: {
      company: "Monthly $500 into XEQT",
      data: "Month 1: $25 = 20 shares; Month 2: $20 = 25 shares; Month 3: $30 = 16.67 shares",
      calculation: "Avg cost: $1,500 ÷ 61.67 = $24.32/share",
      interpretation: "DCA smooths out volatility impact."
    },
    quiz: {
      question: "Dollar-cost averaging helps reduce:",
      options: ["Fees", "Timing risk", "Diversification", "Dividends"],
      correctIndex: 1,
      explanation: "DCA reduces risk of investing all money at a market peak."
    },
    relatedTerms: ["Lump Sum", "Automatic Investing", "Risk Management", "TFSA"]
  },
  // === INVESTMENT VEHICLES ===
  {
    id: "etf",
    term: "ETF (Exchange-Traded Fund)",
    category: "Investment Products",
    definition: "An ETF is a basket of securities that trades on an exchange like a stock. ETFs offer instant diversification, low fees (MER), and tax efficiency. Popular examples: XEQT, VFV, ZSP, QQQ, SPY.",
    formula: "ETF Price ≈ Net Asset Value of holdings",
    formulaVariables: [
      { variable: "NAV", explanation: "Value of underlying holdings" },
      { variable: "MER", explanation: "Annual fee (e.g., 0.20% for XEQT)" }
    ],
    example: {
      company: "XEQT.TO",
      data: "10,000+ stocks, 40+ countries, MER: 0.20%",
      calculation: "One ETF = global stock diversification",
      interpretation: "Perfect one-fund solution for Canadian investors."
    },
    quiz: {
      question: "ETFs trade:",
      options: ["Only at day end", "Throughout market hours", "Once per week", "Only through banks"],
      correctIndex: 1,
      explanation: "Unlike mutual funds, ETFs trade continuously like stocks."
    },
    relatedTerms: ["Index Fund", "MER", "Diversification", "NAV"]
  },
  {
    id: "index-fund",
    term: "Index Fund",
    category: "Investment Products",
    definition: "An index fund passively tracks a market index (like S&P 500) rather than trying to beat it. They offer low fees, broad diversification, and typically outperform active funds over time. Can be ETF or mutual fund.",
    formula: "Index Fund Return ≈ Index Return - Fees",
    formulaVariables: [
      { variable: "Tracking", explanation: "Mirrors index composition" },
      { variable: "Passive", explanation: "No active stock picking" }
    ],
    example: {
      company: "VFV.TO (S&P 500 Index)",
      data: "Tracks 500 largest US companies, MER: 0.09%",
      calculation: "Returns nearly match S&P 500 minus tiny fee",
      interpretation: "Low-cost exposure to US market leaders."
    },
    quiz: {
      question: "Index funds aim to:",
      options: ["Beat the market", "Match the market", "Minimize volatility", "Pay high dividends"],
      correctIndex: 1,
      explanation: "Index funds aim to match (not beat) their benchmark index."
    },
    relatedTerms: ["ETF", "Passive Investing", "S&P 500", "MER"]
  },
  {
    id: "mutual-fund",
    term: "Mutual Fund",
    category: "Investment Products",
    definition: "A mutual fund pools investor money into a professionally managed portfolio. Unlike ETFs, mutual funds trade once daily at NAV. Many have high fees (2%+ MER in Canada). Index mutual funds are cheaper.",
    formula: "NAV = (Total Assets - Liabilities) ÷ Shares Outstanding",
    formulaVariables: [
      { variable: "NAV", explanation: "Calculated daily after market close" },
      { variable: "MER", explanation: "Often 1-2.5% for active Canadian funds" }
    ],
    example: {
      company: "TD e-Series Funds",
      data: "Low-cost index mutual funds, MER: 0.33-0.51%",
      calculation: "Cheaper than typical mutual funds",
      interpretation: "Good option for automatic investing."
    },
    quiz: {
      question: "Mutual fund prices are set:",
      options: ["Every minute", "Hourly", "Once daily after close", "Weekly"],
      correctIndex: 2,
      explanation: "Mutual funds calculate NAV once per day after market close."
    },
    relatedTerms: ["ETF", "NAV", "MER", "Active Management"]
  },
  {
    id: "gic",
    term: "GIC (Guaranteed Investment Certificate)",
    category: "Investment Products",
    definition: "A GIC is a Canadian deposit that pays guaranteed interest for a fixed term. Principal is protected by CDIC insurance (up to $100K). Safe but returns often trail inflation. Good for emergency funds or short-term goals.",
    formula: "GIC Return = Principal × Interest Rate × Term",
    formulaVariables: [
      { variable: "Term", explanation: "1-5 years typically" },
      { variable: "Interest", explanation: "Fixed rate, guaranteed" }
    ],
    example: {
      company: "5-Year GIC",
      data: "$10,000 at 4.5% for 5 years",
      calculation: "Returns: $10,000 × 4.5% × 5 = $2,250",
      interpretation: "Guaranteed $12,250 at maturity (before tax)."
    },
    quiz: {
      question: "GICs are best for:",
      options: ["Long-term growth", "Maximum returns", "Capital preservation", "High-risk investors"],
      correctIndex: 2,
      explanation: "GICs guarantee principal. Ideal for safety-first goals."
    },
    relatedTerms: ["CDIC", "Fixed Income", "Risk-Free", "Emergency Fund"]
  },
  // === TAX CONCEPTS ===
  {
    id: "capital-gains",
    term: "Capital Gains",
    category: "Tax",
    definition: "Capital gains are profits from selling investments. In Canada, only 50% of capital gains are taxable (inclusion rate). Gains in TFSA/RRSP are sheltered. Capital losses can offset gains.",
    formula: "Taxable Gain = (Sale Price - Purchase Price) × 50%",
    formulaVariables: [
      { variable: "Inclusion Rate", explanation: "50% in Canada" },
      { variable: "ACB", explanation: "Adjusted Cost Base (purchase price + fees)" }
    ],
    example: {
      company: "Non-Registered Account",
      data: "Bought: $10K, Sold: $20K, Tax bracket: 40%",
      calculation: "Gain: $10K × 50% = $5K taxable. Tax: $5K × 40% = $2K",
      interpretation: "You keep $8K of your $10K gain."
    },
    quiz: {
      question: "In Canada, what % of capital gains is taxable?",
      options: ["25%", "50%", "75%", "100%"],
      correctIndex: 1,
      explanation: "Canada has a 50% inclusion rate for capital gains."
    },
    relatedTerms: ["Capital Loss", "ACB", "TFSA", "Tax-Loss Harvesting"]
  },
  {
    id: "tax-loss-harvesting",
    term: "Tax-Loss Harvesting",
    category: "Tax",
    definition: "Tax-loss harvesting means selling losing investments to offset capital gains and reduce taxes. Losses can carry forward indefinitely in Canada. The superficial loss rule prevents repurchasing the same security within 30 days.",
    formula: "Tax Savings = Capital Loss × Inclusion Rate × Marginal Rate",
    formulaVariables: [
      { variable: "Capital Loss", explanation: "Loss from selling" },
      { variable: "30-Day Rule", explanation: "Can't repurchase same security" }
    ],
    example: {
      company: "Year-End Strategy",
      data: "Gains: $10K, Losses: $4K, 40% bracket",
      calculation: "Net taxable: ($10K - $4K) × 50% = $3K. Tax: $1,200",
      interpretation: "Saved $800 in taxes by harvesting the loss."
    },
    quiz: {
      question: "Superficial loss rule prevents repurchasing for:",
      options: ["7 days", "30 days", "90 days", "1 year"],
      correctIndex: 1,
      explanation: "30 days before OR after the sale."
    },
    relatedTerms: ["Capital Gains", "Capital Loss", "ACB", "Tax Planning"]
  },
  {
    id: "dividend-tax-credit",
    term: "Dividend Tax Credit",
    category: "Tax",
    definition: "Canadian dividends from Canadian companies receive preferential tax treatment through the dividend tax credit. 'Eligible dividends' from public companies are taxed at lower rates than interest or foreign dividends.",
    formula: "Effective tax rate on eligible dividends is much lower than marginal rate",
    formulaVariables: [
      { variable: "Eligible Dividends", explanation: "From Canadian public companies" },
      { variable: "Gross-Up", explanation: "Dividends are grossed up, then credited" }
    ],
    example: {
      company: "Canadian Bank Dividends",
      data: "$1,000 eligible dividend, 30% marginal rate",
      calculation: "Effective tax: ~$100-200 depending on province",
      interpretation: "Much lower than $300 on interest income."
    },
    quiz: {
      question: "Canadian eligible dividends receive:",
      options: ["No tax credit", "Dividend tax credit", "Double taxation", "Capital gains treatment"],
      correctIndex: 1,
      explanation: "Dividend tax credit reduces effective tax on Canadian dividends."
    },
    relatedTerms: ["Eligible Dividend", "Gross-Up", "Tax Efficiency", "Canadian Stocks"]
  },
  // === CORPORATE ACTIONS ===
  {
    id: "stock-split",
    term: "Stock Split",
    category: "Corporate Actions",
    definition: "A stock split increases shares and decreases price proportionally. A 4:1 split gives 4 shares at 1/4 price for each share owned. Total value unchanged, just more shares at lower prices. Can increase liquidity and accessibility.",
    formula: "New Shares = Old Shares × Split Ratio; New Price = Old Price ÷ Ratio",
    formulaVariables: [
      { variable: "Ratio", explanation: "e.g., 4:1, 3:1, 2:1" },
      { variable: "Total Value", explanation: "Unchanged" }
    ],
    example: {
      company: "Apple 4:1 Split (2020)",
      data: "Pre: 1 share at $500 = $500",
      calculation: "Post: 4 shares at $125 = $500",
      interpretation: "Same value, more accessible price."
    },
    quiz: {
      question: "After a 2:1 split, 100 shares at $60 becomes:",
      options: ["50 shares at $120", "100 shares at $30", "200 shares at $30", "200 shares at $60"],
      correctIndex: 2,
      explanation: "Shares double, price halves: 200 × $30 = $6,000"
    },
    relatedTerms: ["Reverse Split", "Share Price", "Market Cap", "Liquidity"]
  },
  {
    id: "buyback",
    term: "Share Buyback",
    category: "Corporate Actions",
    definition: "A buyback is when a company repurchases its own shares, reducing shares outstanding. This increases EPS and often stock price. Buybacks return cash to shareholders without triggering dividend taxes.",
    formula: "New EPS = Same Earnings ÷ Fewer Shares",
    formulaVariables: [
      { variable: "Shares Repurchased", explanation: "Removed from circulation" },
      { variable: "EPS Impact", explanation: "Higher due to fewer shares" }
    ],
    example: {
      company: "Apple Buybacks",
      data: "Repurchased $600B+ since 2012",
      calculation: "Shares: 23B (2012) → 15B (2024)",
      interpretation: "35% fewer shares = much higher EPS."
    },
    quiz: {
      question: "Share buybacks typically:",
      options: ["Dilute shareholders", "Increase shares outstanding", "Boost EPS", "Reduce cash"],
      correctIndex: 2,
      explanation: "Fewer shares = higher earnings per share."
    },
    relatedTerms: ["EPS", "Capital Allocation", "Dividends", "Return of Capital"]
  },
  // === MORE ADVANCED CONCEPTS ===
  {
    id: "compound-interest",
    term: "Compound Interest",
    category: "Basics",
    definition: "Compound interest is 'interest on interest'. Your returns generate more returns. It's the most powerful force in investing. Starting early maximizes compounding time. $10K at 7% becomes $76K in 30 years.",
    formula: "Future Value = Principal × (1 + Rate)^Years",
    formulaVariables: [
      { variable: "Principal", explanation: "Initial investment" },
      { variable: "Rate", explanation: "Annual return percentage" },
      { variable: "Years", explanation: "Time invested" }
    ],
    example: {
      company: "Early Investor Advantage",
      data: "$10,000 at 7% for 30 years",
      calculation: "$10K × (1.07)^30 = $76,123",
      interpretation: "7x growth from compound interest alone."
    },
    quiz: {
      question: "What did Einstein call compound interest?",
      options: ["The 8th wonder", "Free money", "Investor's dream", "Magic formula"],
      correctIndex: 0,
      explanation: "Einstein allegedly called compound interest 'the 8th wonder of the world.'"
    },
    relatedTerms: ["Time Value of Money", "Rule of 72", "Growth", "Investing Early"]
  },
  {
    id: "rule-of-72",
    term: "Rule of 72",
    category: "Basics",
    definition: "The Rule of 72 estimates how long it takes to double your money. Divide 72 by your expected return rate. At 7% return, money doubles in ~10 years. Simple mental math for quick compounding estimates.",
    formula: "Years to Double = 72 ÷ Annual Return Rate",
    formulaVariables: [
      { variable: "72", explanation: "Magic number for this approximation" },
      { variable: "Return Rate", explanation: "Expected annual percentage" }
    ],
    example: {
      company: "Investment Doubling",
      data: "Expected return: 8%",
      calculation: "72 ÷ 8 = 9 years to double",
      interpretation: "$10K becomes $20K in ~9 years at 8%."
    },
    quiz: {
      question: "At 6% return, how long to double your money?",
      options: ["6 years", "12 years", "18 years", "72 years"],
      correctIndex: 1,
      explanation: "72 ÷ 6 = 12 years to double."
    },
    relatedTerms: ["Compound Interest", "Growth Rate", "Investing", "Time Value"]
  },
  {
    id: "liquidity",
    term: "Liquidity",
    category: "Basics",
    definition: "Liquidity measures how easily an asset can be converted to cash without significant price impact. Cash is most liquid; real estate is illiquid. Highly traded stocks (AAPL, MSFT) are very liquid; penny stocks are not.",
    formula: "Liquidity indicators: Trading volume, bid-ask spread, market cap",
    formulaVariables: [
      { variable: "Volume", explanation: "Higher = more liquid" },
      { variable: "Bid-Ask Spread", explanation: "Tighter = more liquid" }
    ],
    example: {
      company: "AAPL vs Penny Stock",
      data: "AAPL: 50M shares/day; Penny stock: 10K shares/day",
      calculation: "AAPL is 5,000x more liquid",
      interpretation: "You can sell AAPL instantly without moving the price."
    },
    quiz: {
      question: "Which is MOST liquid?",
      options: ["Real estate", "Penny stocks", "S&P 500 ETF (SPY)", "Private equity"],
      correctIndex: 2,
      explanation: "SPY is extremely liquid with massive daily volume."
    },
    relatedTerms: ["Volume", "Bid-Ask Spread", "Market Cap", "Trading"]
  },
  {
    id: "bid-ask-spread",
    term: "Bid-Ask Spread",
    category: "Basics",
    definition: "The bid is the highest price buyers will pay; the ask is the lowest price sellers accept. The spread is the difference. Tighter spreads indicate more liquidity. You buy at the ask and sell at the bid.",
    formula: "Spread = Ask Price - Bid Price",
    formulaVariables: [
      { variable: "Bid", explanation: "Price buyers offer" },
      { variable: "Ask", explanation: "Price sellers want" }
    ],
    example: {
      company: "XEQT.TO",
      data: "Bid: $29.95, Ask: $29.97",
      calculation: "Spread: $0.02 (0.07%)",
      interpretation: "Very tight spread = high liquidity, low trading cost."
    },
    quiz: {
      question: "If bid is $50 and ask is $50.10, you BUY at:",
      options: ["$50.00", "$50.05", "$50.10", "$49.90"],
      correctIndex: 2,
      explanation: "You buy at the ask (seller's price)."
    },
    relatedTerms: ["Liquidity", "Market Order", "Limit Order", "Trading"]
  },
  {
    id: "market-order",
    term: "Market Order",
    category: "Trading",
    definition: "A market order executes immediately at the best available price. Fast but you don't control the exact price. Good for liquid stocks; risky for illiquid ones. Use limit orders for more control.",
    formula: "Execution = Immediate at current market price",
    formulaVariables: [
      { variable: "Speed", explanation: "Instant execution" },
      { variable: "Price", explanation: "Best available (not guaranteed)" }
    ],
    example: {
      company: "Buying XEQT",
      data: "Market order for 100 shares, ask: $30.00",
      calculation: "Executes at ~$30.00 immediately",
      interpretation: "Fast but might pay slightly more than expected."
    },
    quiz: {
      question: "Market orders prioritize:",
      options: ["Best price", "Fast execution", "Lower fees", "Timing"],
      correctIndex: 1,
      explanation: "Market orders prioritize speed over price control."
    },
    relatedTerms: ["Limit Order", "Bid-Ask", "Execution", "Slippage"]
  },
  {
    id: "limit-order",
    term: "Limit Order",
    category: "Trading",
    definition: "A limit order only executes at your specified price or better. You control the price but execution isn't guaranteed. Use for less liquid stocks or when you want a specific entry point.",
    formula: "Buy limit executes at limit price or LOWER; Sell limit at limit or HIGHER",
    formulaVariables: [
      { variable: "Limit Price", explanation: "Maximum you'll pay (buy) or minimum you'll accept (sell)" },
      { variable: "Execution", explanation: "Only if market reaches your price" }
    ],
    example: {
      company: "XEQT Limit Order",
      data: "Limit buy at $29.50 when current price is $30.00",
      calculation: "Only executes if price drops to $29.50",
      interpretation: "May not fill, but guarantees your price."
    },
    quiz: {
      question: "A limit order guarantees:",
      options: ["Execution", "Price", "Both", "Neither"],
      correctIndex: 1,
      explanation: "Limit orders guarantee price but not execution."
    },
    relatedTerms: ["Market Order", "Stop Order", "Execution", "Trading"]
  },
  // === ADDITIONAL IMPORTANT TERMS ===
  {
    id: "nav",
    term: "NAV (Net Asset Value)",
    category: "Investment Products",
    definition: "NAV is the per-share value of a fund's assets minus liabilities. Mutual funds trade at NAV; ETFs trade at market prices near NAV. NAV is calculated daily for mutual funds, continuously for ETFs.",
    formula: "NAV = (Total Assets - Total Liabilities) ÷ Shares Outstanding",
    formulaVariables: [
      { variable: "Assets", explanation: "Value of all holdings" },
      { variable: "Liabilities", explanation: "Fund expenses and obligations" }
    ],
    example: {
      company: "TD Canadian Index Fund",
      data: "Assets: $5B, Liabilities: $50M, Shares: 200M",
      calculation: "NAV = ($5B - $50M) ÷ 200M = $24.75",
      interpretation: "Each share represents $24.75 of underlying assets."
    },
    quiz: {
      question: "NAV is most important for:",
      options: ["Individual stocks", "Mutual funds and ETFs", "Bonds only", "Options"],
      correctIndex: 1,
      explanation: "NAV represents the per-share value of fund holdings."
    },
    relatedTerms: ["ETF", "Mutual Fund", "Premium/Discount", "Holdings"]
  },
  {
    id: "yield",
    term: "Yield",
    category: "Basics",
    definition: "Yield is the income return on an investment, expressed as a percentage. For stocks, it's dividend yield. For bonds, it's yield to maturity. Higher yields often come with higher risk.",
    formula: "Yield = (Annual Income ÷ Price) × 100%",
    formulaVariables: [
      { variable: "Income", explanation: "Dividends or interest received" },
      { variable: "Price", explanation: "Current market price" }
    ],
    example: {
      company: "Bond Yield",
      data: "$1,000 bond paying $40/year interest",
      calculation: "Yield = $40 ÷ $1,000 = 4%",
      interpretation: "The bond yields 4% annually."
    },
    quiz: {
      question: "Yield measures:",
      options: ["Price change", "Income return", "Total return", "Risk level"],
      correctIndex: 1,
      explanation: "Yield is the income component of returns."
    },
    relatedTerms: ["Dividend Yield", "Yield to Maturity", "Income", "Return"]
  },
  {
    id: "portfolio",
    term: "Portfolio",
    category: "Basics",
    definition: "A portfolio is your collection of investments. It can include stocks, bonds, ETFs, cash, and other assets. Portfolio construction balances risk and return according to your goals and risk tolerance.",
    formula: "Portfolio Value = Sum of all investment values",
    formulaVariables: [
      { variable: "Holdings", explanation: "Individual investments" },
      { variable: "Allocation", explanation: "Percentage in each asset" }
    ],
    example: {
      company: "Simple Canadian Portfolio",
      data: "80% XEQT, 20% ZAG",
      calculation: "Growth-oriented with some bond stability",
      interpretation: "Common allocation for long-term Canadian investors."
    },
    quiz: {
      question: "A well-diversified portfolio should have:",
      options: ["One stock", "Only bonds", "Mix of asset classes", "100% cash"],
      correctIndex: 2,
      explanation: "Diversification means mixing different asset types."
    },
    relatedTerms: ["Diversification", "Asset Allocation", "Holdings", "Risk"]
  },
  {
    id: "benchmark",
    term: "Benchmark",
    category: "Portfolio Management",
    definition: "A benchmark is a standard to measure investment performance against. The S&P 500 is a common stock benchmark; bond indices for fixed income. Beating your benchmark means outperforming the market.",
    formula: "Relative Performance = Portfolio Return - Benchmark Return",
    formulaVariables: [
      { variable: "Benchmark", explanation: "Reference index (S&P 500, TSX, etc.)" },
      { variable: "Alpha", explanation: "Excess return vs benchmark" }
    ],
    example: {
      company: "Canadian Equity Fund",
      data: "Fund return: 12%, TSX Composite: 10%",
      calculation: "Outperformed by 2%",
      interpretation: "Positive alpha of 2% vs Canadian market."
    },
    quiz: {
      question: "A benchmark is used to:",
      options: ["Set prices", "Measure relative performance", "Calculate dividends", "Determine splits"],
      correctIndex: 1,
      explanation: "Benchmarks help you see if you're beating or lagging the market."
    },
    relatedTerms: ["Alpha", "Index", "S&P 500", "Performance"]
  },
  {
    id: "sharpe-ratio",
    term: "Sharpe Ratio",
    category: "Portfolio Management",
    definition: "Sharpe ratio measures risk-adjusted return: excess return per unit of risk. Higher is better. Above 1 is good; above 2 is very good. Helps compare investments with different risk levels.",
    formula: "Sharpe = (Portfolio Return - Risk-Free Rate) ÷ Standard Deviation",
    formulaVariables: [
      { variable: "Excess Return", explanation: "Return above risk-free rate" },
      { variable: "Std Dev", explanation: "Volatility (risk)" }
    ],
    example: {
      company: "Fund Comparison",
      data: "Fund A: 12% return, 15% vol; Fund B: 8% return, 5% vol; Risk-free: 4%",
      calculation: "A: (12-4)/15 = 0.53; B: (8-4)/5 = 0.80",
      interpretation: "Fund B has better risk-adjusted returns despite lower return."
    },
    quiz: {
      question: "A Sharpe ratio of 1.5 means:",
      options: ["Poor performance", "Good risk-adjusted return", "High risk", "Negative return"],
      correctIndex: 1,
      explanation: "Sharpe > 1 is good; 1.5 indicates solid risk-adjusted returns."
    },
    relatedTerms: ["Alpha", "Risk-Adjusted Return", "Volatility", "Standard Deviation"]
  },
  {
    id: "alpha",
    term: "Alpha",
    category: "Portfolio Management",
    definition: "Alpha measures excess return above what's expected for the risk taken. Positive alpha means outperformance; negative means underperformance. Generating consistent alpha is extremely difficult. Most active managers fail.",
    formula: "Alpha = Actual Return - Expected Return (based on beta)",
    formulaVariables: [
      { variable: "Actual", explanation: "What you actually earned" },
      { variable: "Expected", explanation: "What beta predicts" }
    ],
    example: {
      company: "Active Fund",
      data: "Fund: 15%, S&P 500: 12%, Fund beta: 1.0",
      calculation: "Alpha = 15% - 12% = +3%",
      interpretation: "Manager generated 3% excess return."
    },
    quiz: {
      question: "Positive alpha indicates:",
      options: ["Higher volatility", "Outperformance vs benchmark", "More risk", "Lower fees"],
      correctIndex: 1,
      explanation: "Alpha = excess return above benchmark."
    },
    relatedTerms: ["Beta", "Benchmark", "Active Management", "Excess Return"]
  },
  {
    id: "short-selling",
    term: "Short Selling",
    category: "Trading",
    definition: "Short selling means borrowing shares to sell, hoping to buy them back cheaper later. You profit when prices fall. Risky because losses are theoretically unlimited if the stock rises. Advanced strategy.",
    formula: "Short Profit = (Sell Price - Buy Price) - Borrowing Costs",
    formulaVariables: [
      { variable: "Borrow", explanation: "Shares borrowed from broker" },
      { variable: "Cover", explanation: "Buying back to return shares" }
    ],
    example: {
      company: "Short Trade",
      data: "Short sell at $100, cover at $80",
      calculation: "Profit = $100 - $80 = $20 per share",
      interpretation: "Made $20 as the stock fell."
    },
    quiz: {
      question: "Short sellers profit when:",
      options: ["Prices rise", "Prices fall", "Dividends are paid", "Splits occur"],
      correctIndex: 1,
      explanation: "Shorts sell high, buy low = profit when prices drop."
    },
    relatedTerms: ["Long Position", "Short Squeeze", "Margin", "Borrowing"]
  },
  {
    id: "margin",
    term: "Margin (Trading)",
    category: "Trading",
    definition: "Margin is borrowed money from your broker to buy more securities. It amplifies both gains and losses. A margin call occurs when your account value drops below the required minimum. High risk for beginners.",
    formula: "Buying Power = Cash + Margin Available",
    formulaVariables: [
      { variable: "Margin Rate", explanation: "Interest on borrowed funds" },
      { variable: "Margin Call", explanation: "Forced sale if equity drops too low" }
    ],
    example: {
      company: "Margin Example",
      data: "$10K cash + $10K margin = $20K buying power",
      calculation: "If stocks drop 25%, you lose $5K (50% of your cash)",
      interpretation: "Leverage magnifies losses."
    },
    quiz: {
      question: "Margin trading is:",
      options: ["Risk-free", "Using borrowed money", "Only for ETFs", "Required for all trades"],
      correctIndex: 1,
      explanation: "Margin = trading with borrowed money from your broker."
    },
    relatedTerms: ["Leverage", "Margin Call", "Short Selling", "Risk"]
  },
  // Filling in a few more essential terms to reach 100+
  {
    id: "ex-dividend-date",
    term: "Ex-Dividend Date",
    category: "Dividends",
    definition: "The ex-dividend date is the cutoff for receiving the next dividend payment. You must own shares BEFORE this date to get the dividend. On the ex-date, stock price typically drops by the dividend amount.",
    formula: "Must own shares before ex-dividend date to receive payment",
    formulaVariables: [
      { variable: "Record Date", explanation: "Date company checks ownership" },
      { variable: "Payment Date", explanation: "When dividend is actually paid" }
    ],
    example: {
      company: "TD Bank Dividend",
      data: "Ex-div: Jan 8, Record: Jan 9, Payment: Jan 31",
      calculation: "Buy by Jan 7 to receive the dividend",
      interpretation: "Buying on Jan 8 (ex-date) means you miss this dividend."
    },
    quiz: {
      question: "To receive a dividend, you must own shares:",
      options: ["On the payment date", "Before the ex-dividend date", "On the record date", "For at least 1 year"],
      correctIndex: 1,
      explanation: "Must own BEFORE ex-dividend date."
    },
    relatedTerms: ["Dividend", "Record Date", "Payment Date", "Dividend Capture"]
  },
  {
    id: "aum",
    term: "AUM (Assets Under Management)",
    category: "Investment Products",
    definition: "AUM is the total market value of investments a fund manages. Larger AUM often means more liquidity and lower costs due to scale. Very large AUM can make it harder for active managers to outperform.",
    formula: "AUM = Sum of all investor assets in the fund",
    formulaVariables: [
      { variable: "Size", explanation: "Total capital managed" },
      { variable: "Flows", explanation: "Net inflows or outflows" }
    ],
    example: {
      company: "Vanguard Total Stock Market ETF (VTI)",
      data: "AUM: $400+ Billion",
      calculation: "One of the largest ETFs in the world",
      interpretation: "Massive scale = extremely low fees (0.03%)."
    },
    quiz: {
      question: "Larger AUM typically means:",
      options: ["Higher fees", "More liquidity", "Higher returns", "More risk"],
      correctIndex: 1,
      explanation: "Larger funds usually have better liquidity and lower fees."
    },
    relatedTerms: ["ETF", "Mutual Fund", "Liquidity", "Scale"]
  },
  // === MORE TRADING & INVESTING TERMS ===
  {
    id: "stop-loss",
    term: "Stop-Loss Order",
    category: "Trading",
    definition: "A stop-loss order automatically sells a stock when it falls to a specific price, limiting potential losses. It's a risk management tool that removes emotion from selling decisions.",
    formula: "Stop-Loss Price = Purchase Price × (1 - Loss Tolerance %)",
    formulaVariables: [
      { variable: "Purchase Price", explanation: "Price you bought at" },
      { variable: "Loss Tolerance", explanation: "Maximum % loss you'll accept (e.g., 10%)" }
    ],
    example: {
      company: "Example Trade",
      data: "Bought AAPL at $200, 10% stop-loss",
      calculation: "Stop at $200 × 0.90 = $180",
      interpretation: "If AAPL drops to $180, it auto-sells to limit your loss."
    },
    quiz: {
      question: "A stop-loss order is primarily used to:",
      options: ["Maximize gains", "Limit potential losses", "Buy at lower prices", "Earn dividends"],
      correctIndex: 1,
      explanation: "Stop-losses automatically sell to limit downside risk."
    },
    relatedTerms: ["Limit Order", "Market Order", "Risk Management", "Trailing Stop"]
  },
  {
    id: "trailing-stop",
    term: "Trailing Stop",
    category: "Trading",
    definition: "A trailing stop moves up with the stock price but doesn't move down. It locks in gains while protecting against reversals. If set at 10%, it triggers when price drops 10% from its highest point since you bought.",
    formula: "Trigger Price = Highest Price Since Purchase × (1 - Trail %)",
    formulaVariables: [
      { variable: "Trail %", explanation: "Distance from high that triggers sale" },
      { variable: "High Price", explanation: "Highest price reached" }
    ],
    example: {
      company: "NVDA Position",
      data: "Bought at $400, rose to $600, 15% trail",
      calculation: "Stop rises to $600 × 0.85 = $510",
      interpretation: "You're guaranteed at least $510 (27.5% gain) if it reverses."
    },
    quiz: {
      question: "A trailing stop:",
      options: ["Moves down with price", "Stays fixed", "Moves up with price gains", "Only triggers on gains"],
      correctIndex: 2,
      explanation: "Trailing stops rise with price but never fall."
    },
    relatedTerms: ["Stop-Loss", "Risk Management", "Profit Protection", "Limit Order"]
  },
  {
    id: "market-maker",
    term: "Market Maker",
    category: "Trading",
    definition: "A market maker provides liquidity by continuously offering to buy and sell stocks. They profit from the bid-ask spread. Major market makers include Citadel Securities, Virtu Financial, and big banks.",
    formula: "Market Maker Profit = Ask Price - Bid Price (Spread)",
    formulaVariables: [
      { variable: "Bid", explanation: "Price they'll buy at" },
      { variable: "Ask", explanation: "Price they'll sell at" }
    ],
    example: {
      company: "Stock XYZ",
      data: "Bid: $99.95, Ask: $100.05",
      calculation: "Spread = $0.10 per share profit potential",
      interpretation: "Market maker earns ~$0.10 per round-trip trade."
    },
    quiz: {
      question: "Market makers profit from:",
      options: ["Dividends", "Price appreciation", "The bid-ask spread", "Management fees"],
      correctIndex: 2,
      explanation: "They earn the spread between buy and sell prices."
    },
    relatedTerms: ["Bid-Ask Spread", "Liquidity", "Order Flow", "Exchange"]
  },
  {
    id: "dark-pool",
    term: "Dark Pool",
    category: "Trading",
    definition: "Dark pools are private exchanges where large trades happen anonymously without impacting public market prices. Institutional investors use them to buy/sell large blocks without moving the market.",
    formula: "Dark Pool Volume ≈ 40% of US equity trading",
    formulaVariables: [
      { variable: "Block Trade", explanation: "Large orders (10,000+ shares)" },
      { variable: "Price Impact", explanation: "Minimized in dark pools" }
    ],
    example: {
      company: "Pension Fund Trade",
      data: "Need to sell 1M shares of MSFT",
      calculation: "On public exchange: price drops. Dark pool: anonymous.",
      interpretation: "Dark pools help big investors avoid moving the market."
    },
    quiz: {
      question: "Dark pools are primarily used by:",
      options: ["Retail investors", "Day traders", "Institutional investors", "Market makers"],
      correctIndex: 2,
      explanation: "Institutions use dark pools for large, anonymous trades."
    },
    relatedTerms: ["Institutional Investor", "Block Trade", "Liquidity", "Exchange"]
  },
  {
    id: "hft",
    term: "High-Frequency Trading (HFT)",
    category: "Trading",
    definition: "HFT uses powerful computers to execute thousands of trades per second, profiting from tiny price discrepancies. HFT firms account for about 50% of US equity volume. Controversial but adds liquidity.",
    formula: "Speed advantage measured in microseconds (millionths of a second)",
    formulaVariables: [
      { variable: "Latency", explanation: "Time to execute trades" },
      { variable: "Arbitrage", explanation: "Exploiting price differences" }
    ],
    example: {
      company: "HFT Strategy",
      data: "Profit: $0.001 per share × millions of shares",
      calculation: "1M shares × $0.001 = $1,000 profit",
      interpretation: "Small margins, massive volume = significant profits."
    },
    quiz: {
      question: "HFT executes trades in:",
      options: ["Minutes", "Seconds", "Milliseconds or microseconds", "Hours"],
      correctIndex: 2,
      explanation: "HFT operates in milliseconds or faster."
    },
    relatedTerms: ["Algorithmic Trading", "Market Maker", "Arbitrage", "Latency"]
  },
  {
    id: "ipo",
    term: "IPO (Initial Public Offering)",
    category: "Corporate Actions",
    definition: "An IPO is when a private company first sells shares to the public. It's how companies raise capital and how early investors cash out. IPOs can be volatile. Prices often jump or crash on day one.",
    formula: "IPO Price set by underwriters based on company valuation",
    formulaVariables: [
      { variable: "Valuation", explanation: "Company worth before IPO" },
      { variable: "Float", explanation: "Shares offered to public" }
    ],
    example: {
      company: "Shopify IPO (2015)",
      data: "IPO price: $17 CAD",
      calculation: "Now worth 5x+ (adjusted for splits)",
      interpretation: "Early IPO investors saw massive gains."
    },
    quiz: {
      question: "An IPO is when a company:",
      options: ["Goes bankrupt", "First sells shares publicly", "Pays a dividend", "Does a stock split"],
      correctIndex: 1,
      explanation: "IPO = Initial Public Offering, first public sale."
    },
    relatedTerms: ["Underwriter", "Primary Market", "Float", "Lock-Up Period"]
  },
  {
    id: "secondary-offering",
    term: "Secondary Offering",
    category: "Corporate Actions",
    definition: "A secondary offering is when a company sells additional shares after its IPO. It can dilute existing shareholders but raises capital for growth. Watch for 'at-the-market' offerings that slowly sell shares.",
    formula: "Dilution = New Shares ÷ (Old Shares + New Shares)",
    formulaVariables: [
      { variable: "New Shares", explanation: "Shares being issued" },
      { variable: "Dilution", explanation: "Reduction in ownership %" }
    ],
    example: {
      company: "Growth Company",
      data: "100M shares outstanding, issues 20M more",
      calculation: "Dilution = 20M ÷ 120M = 16.7%",
      interpretation: "Existing shareholders now own 16.7% less of the company."
    },
    quiz: {
      question: "A secondary offering typically:",
      options: ["Increases share price", "Dilutes existing shareholders", "Pays dividends", "Reduces debt"],
      correctIndex: 1,
      explanation: "More shares = each share represents smaller ownership."
    },
    relatedTerms: ["IPO", "Dilution", "Capital Raise", "Shares Outstanding"]
  },
  {
    id: "blue-chip",
    term: "Blue-Chip Stock",
    category: "Basics",
    definition: "Blue-chip stocks are shares of large, well-established companies with stable earnings and dividends. Examples include Apple, Microsoft, Royal Bank, and TD. They're considered safer but offer lower growth potential.",
    formula: "Blue-chip criteria: Market cap > $10B, consistent dividends, industry leader",
    formulaVariables: [
      { variable: "Market Cap", explanation: "Usually $10B+ (large-cap)" },
      { variable: "Track Record", explanation: "Decades of profitability" }
    ],
    example: {
      company: "Canadian Blue Chips",
      data: "RY.TO, TD.TO, ENB.TO, CNR.TO",
      calculation: "All pay dividends, trade on TSX for decades",
      interpretation: "Core holdings for conservative Canadian portfolios."
    },
    quiz: {
      question: "Blue-chip stocks are typically:",
      options: ["Small startups", "Highly speculative", "Large, established companies", "Penny stocks"],
      correctIndex: 2,
      explanation: "Blue chips are established industry leaders."
    },
    relatedTerms: ["Large Cap", "Dividend Aristocrat", "Defensive Stock", "Quality"]
  },
  {
    id: "penny-stock",
    term: "Penny Stock",
    category: "Basics",
    definition: "Penny stocks trade under $5 (or sometimes $1) and often on smaller exchanges. They're highly speculative, volatile, and prone to manipulation. Most financial advisors warn against them for beginners.",
    formula: "Risk Level = Extremely High (can lose 100%)",
    formulaVariables: [
      { variable: "Price", explanation: "Usually under $5" },
      { variable: "Volume", explanation: "Often very low (illiquid)" }
    ],
    example: {
      company: "Penny Stock Risks",
      data: "Company XYZ trades at $0.10",
      calculation: "Can go to $0.01 or $0 (delisted)",
      interpretation: "90%+ loss potential. Not for beginners."
    },
    quiz: {
      question: "Penny stocks are considered:",
      options: ["Safe investments", "Blue-chip quality", "Highly speculative", "Government bonds"],
      correctIndex: 2,
      explanation: "Penny stocks are among the riskiest investments."
    },
    relatedTerms: ["Speculation", "Volatility", "OTC", "Pump and Dump"]
  },
  {
    id: "growth-stock",
    term: "Growth Stock",
    category: "Investment Products",
    definition: "Growth stocks prioritize reinvesting profits over paying dividends. They aim for rapid revenue/earnings growth. Examples: Tesla, Shopify, Amazon. Higher risk/reward than value stocks.",
    formula: "Growth Stock = High P/E + High Revenue Growth + Low/No Dividend",
    formulaVariables: [
      { variable: "P/E Ratio", explanation: "Usually high (30+)" },
      { variable: "Revenue Growth", explanation: "20%+ annually" }
    ],
    example: {
      company: "Shopify (SHOP)",
      data: "P/E: 60+, Revenue growth: 25%+, No dividend",
      calculation: "Classic growth stock profile",
      interpretation: "You're betting on future earnings, not current income."
    },
    quiz: {
      question: "Growth stocks typically:",
      options: ["Pay high dividends", "Have low P/E ratios", "Reinvest profits for expansion", "Are very safe"],
      correctIndex: 2,
      explanation: "Growth companies reinvest rather than pay dividends."
    },
    relatedTerms: ["Value Stock", "P/E Ratio", "GARP", "Tech Stocks"]
  },
  {
    id: "value-stock",
    term: "Value Stock",
    category: "Investment Products",
    definition: "Value stocks trade at low prices relative to fundamentals (earnings, book value). Value investors like Warren Buffett seek undervalued companies. Often mature companies with dividends.",
    formula: "Value Stock = Low P/E + Low P/B + Often pays dividends",
    formulaVariables: [
      { variable: "P/E Ratio", explanation: "Below market average" },
      { variable: "P/B Ratio", explanation: "Often below 1.5" }
    ],
    example: {
      company: "Canadian Banks",
      data: "TD.TO: P/E ~11, P/B ~1.3, 4%+ dividend",
      calculation: "Classic value stock characteristics",
      interpretation: "Trading at discount to growth stocks with steady income."
    },
    quiz: {
      question: "Value investors seek stocks that are:",
      options: ["Overvalued", "Undervalued relative to fundamentals", "No earnings", "Maximum volatility"],
      correctIndex: 1,
      explanation: "Value investing = buying undervalued companies."
    },
    relatedTerms: ["Growth Stock", "P/E Ratio", "Intrinsic Value", "Warren Buffett"]
  },
  {
    id: "dividend-aristocrat",
    term: "Dividend Aristocrat",
    category: "Dividends",
    definition: "A Dividend Aristocrat has increased its dividend for 25+ consecutive years. These are the most reliable income stocks. S&P 500 Dividend Aristocrats include Johnson & Johnson, Coca-Cola, and 3M.",
    formula: "Criteria: 25+ years of consecutive dividend increases",
    formulaVariables: [
      { variable: "Track Record", explanation: "Must increase dividend annually" },
      { variable: "S&P 500", explanation: "Must be S&P 500 member" }
    ],
    example: {
      company: "Canadian Dividend Growers",
      data: "Fortis (FTS.TO): 50+ years of increases",
      calculation: "One of Canada's longest dividend growth streaks",
      interpretation: "Extreme reliability for income investors."
    },
    quiz: {
      question: "A Dividend Aristocrat has raised dividends for:",
      options: ["5 years", "10 years", "25+ years", "50+ years required"],
      correctIndex: 2,
      explanation: "25+ consecutive years of dividend increases."
    },
    relatedTerms: ["Dividend", "Dividend Yield", "Income Investing", "DRIP"]
  },
  {
    id: "payout-ratio",
    term: "Payout Ratio",
    category: "Dividends",
    definition: "Payout ratio shows what percentage of earnings a company pays as dividends. Under 60% is generally sustainable; over 100% means paying more than they earn (unsustainable long-term).",
    formula: "Payout Ratio = (Dividends Per Share ÷ EPS) × 100%",
    formulaVariables: [
      { variable: "DPS", explanation: "Annual dividends per share" },
      { variable: "EPS", explanation: "Earnings per share" }
    ],
    example: {
      company: "Royal Bank (RY.TO)",
      data: "DPS: $5.52, EPS: $11.50",
      calculation: "Payout = $5.52 ÷ $11.50 = 48%",
      interpretation: "Healthy 48% payout. Plenty of room for increases."
    },
    quiz: {
      question: "A payout ratio over 100% means:",
      options: ["Very safe dividend", "Paying more than earnings", "High growth", "Low debt"],
      correctIndex: 1,
      explanation: "Over 100% = unsustainable without debt or reserves."
    },
    relatedTerms: ["Dividend", "EPS", "Dividend Coverage", "Sustainability"]
  },
  {
    id: "yield-on-cost",
    term: "Yield on Cost",
    category: "Dividends",
    definition: "Yield on cost measures your dividend return based on what you originally paid, not current price. If you bought at $50 and now get $5 in dividends, your yield on cost is 10% even if current yield is 3%.",
    formula: "Yield on Cost = (Current Annual Dividend ÷ Original Purchase Price) × 100%",
    formulaVariables: [
      { variable: "Current Dividend", explanation: "Today's annual dividend" },
      { variable: "Cost Basis", explanation: "What you paid" }
    ],
    example: {
      company: "Long-term AAPL holder",
      data: "Bought at $20 (split-adjusted), now pays $0.96",
      calculation: "YOC = $0.96 ÷ $20 = 4.8%",
      interpretation: "Current yield is 0.5%, but YOUR yield is 4.8%!"
    },
    quiz: {
      question: "Yield on cost is based on:",
      options: ["Current price", "Future price", "Your purchase price", "Market average"],
      correctIndex: 2,
      explanation: "YOC uses your original cost basis, not current price."
    },
    relatedTerms: ["Dividend Yield", "Cost Basis", "Long-Term Investing", "Compounding"]
  },
  {
    id: "book-value",
    term: "Book Value",
    category: "Fundamental Analysis",
    definition: "Book value is a company's net assets: total assets minus total liabilities. Book value per share shows what shareholders would receive if the company liquidated. Key metric for valuing banks.",
    formula: "Book Value Per Share = (Total Assets - Total Liabilities) ÷ Shares",
    formulaVariables: [
      { variable: "Assets", explanation: "Everything company owns" },
      { variable: "Liabilities", explanation: "Everything company owes" }
    ],
    example: {
      company: "TD Bank (TD.TO)",
      data: "Assets: $1.9T, Liabilities: $1.8T, Shares: 1.8B",
      calculation: "Book Value = ($1.9T - $1.8T) ÷ 1.8B ≈ $55/share",
      interpretation: "TD's tangible book value supports its stock price."
    },
    quiz: {
      question: "Book value equals:",
      options: ["Market cap", "Assets minus liabilities", "Revenue", "Stock price"],
      correctIndex: 1,
      explanation: "Book value = net assets (assets - liabilities)."
    },
    relatedTerms: ["P/B Ratio", "Net Assets", "Liquidation Value", "Equity"]
  },
  {
    id: "intrinsic-value",
    term: "Intrinsic Value",
    category: "Valuation",
    definition: "Intrinsic value is what a stock is actually worth based on fundamentals (cash flows, assets, earnings), regardless of current market price. Value investors buy when price < intrinsic value.",
    formula: "Intrinsic Value = Present Value of All Future Cash Flows",
    formulaVariables: [
      { variable: "DCF", explanation: "Discounted Cash Flow method" },
      { variable: "Discount Rate", explanation: "Required return (8-12%)" }
    ],
    example: {
      company: "Warren Buffett's Approach",
      data: "Market price: $80, Calculated intrinsic value: $120",
      calculation: "Margin of safety = ($120-$80)/$120 = 33%",
      interpretation: "Stock is 33% undervalued. A potential buy."
    },
    quiz: {
      question: "Intrinsic value is:",
      options: ["The current stock price", "The true underlying worth", "The 52-week high", "The IPO price"],
      correctIndex: 1,
      explanation: "Intrinsic value = fundamental worth regardless of market price."
    },
    relatedTerms: ["DCF", "Margin of Safety", "Value Investing", "Fair Value"]
  },
  {
    id: "margin-of-safety",
    term: "Margin of Safety",
    category: "Valuation",
    definition: "Margin of safety is the difference between intrinsic value and market price. Buying with a margin of safety (e.g., paying $70 for something worth $100) protects against errors and downside.",
    formula: "Margin of Safety = (Intrinsic Value - Market Price) ÷ Intrinsic Value",
    formulaVariables: [
      { variable: "Buffer", explanation: "Protection against valuation errors" },
      { variable: "Target", explanation: "Value investors seek 20-50%" }
    ],
    example: {
      company: "Value Investment",
      data: "Intrinsic value: $100, You pay: $65",
      calculation: "Margin = ($100-$65)/$100 = 35%",
      interpretation: "35% cushion if your valuation is off."
    },
    quiz: {
      question: "A larger margin of safety means:",
      options: ["More risk", "Less protection", "More downside protection", "Higher price"],
      correctIndex: 2,
      explanation: "Bigger margin = more room for error and protection."
    },
    relatedTerms: ["Intrinsic Value", "Value Investing", "Ben Graham", "Risk Management"]
  },
  {
    id: "dcf",
    term: "DCF (Discounted Cash Flow)",
    category: "Valuation",
    definition: "DCF values a company by projecting future cash flows and discounting them to present value. It's the most rigorous valuation method. Requires assumptions about growth rates and discount rates.",
    formula: "DCF Value = Σ (Future Cash Flow ÷ (1 + Discount Rate)^n)",
    formulaVariables: [
      { variable: "Cash Flows", explanation: "Projected future FCF" },
      { variable: "Discount Rate", explanation: "WACC or required return" },
      { variable: "n", explanation: "Years into future" }
    ],
    example: {
      company: "Company Valuation",
      data: "FCF: $100M growing 5%, Discount rate: 10%",
      calculation: "Terminal value + discounted cash flows = fair value",
      interpretation: "Complex but most accurate valuation method."
    },
    quiz: {
      question: "DCF stands for:",
      options: ["Daily Cash Flow", "Discounted Cash Flow", "Dividend Compound Factor", "Debt Coverage Factor"],
      correctIndex: 1,
      explanation: "Discounted Cash Flow analysis."
    },
    relatedTerms: ["Intrinsic Value", "WACC", "Terminal Value", "NPV"]
  },
  {
    id: "wacc",
    term: "WACC (Weighted Average Cost of Capital)",
    category: "Fundamental Analysis",
    definition: "WACC is a company's average cost of financing (debt and equity). It's used as the discount rate in DCF. Lower WACC means cheaper capital and higher valuations. Typically 6-12% for mature companies.",
    formula: "WACC = (E/V × Re) + (D/V × Rd × (1-Tax))",
    formulaVariables: [
      { variable: "E", explanation: "Market value of equity" },
      { variable: "D", explanation: "Market value of debt" },
      { variable: "Re/Rd", explanation: "Cost of equity/debt" }
    ],
    example: {
      company: "Mature Company",
      data: "Cost of equity: 10%, Cost of debt: 5%, 50/50 mix",
      calculation: "WACC ≈ (0.5 × 10%) + (0.5 × 5% × 0.75) = 6.9%",
      interpretation: "Used to discount future cash flows in DCF."
    },
    quiz: {
      question: "WACC is used as the:",
      options: ["Interest rate", "Dividend rate", "Discount rate in DCF", "Tax rate"],
      correctIndex: 2,
      explanation: "WACC = discount rate for DCF valuations."
    },
    relatedTerms: ["DCF", "Cost of Capital", "Discount Rate", "Beta"]
  },
  {
    id: "moat",
    term: "Economic Moat",
    category: "Fundamental Analysis",
    definition: "A moat is a sustainable competitive advantage that protects a company from competitors. Moats include brand power, network effects, cost advantages, and switching costs. Buffett looks for 'wide moats.'",
    formula: "Moat Types: Brand, Network, Cost, Switching, Patents",
    formulaVariables: [
      { variable: "Durability", explanation: "How long advantage lasts" },
      { variable: "Width", explanation: "How strong the protection" }
    ],
    example: {
      company: "Apple's Moat",
      data: "Brand loyalty, iOS ecosystem, switching costs",
      calculation: "Customers rarely leave Apple ecosystem",
      interpretation: "Wide moat = pricing power and customer retention."
    },
    quiz: {
      question: "An economic moat is:",
      options: ["A water barrier", "A sustainable competitive advantage", "A type of bond", "A dividend policy"],
      correctIndex: 1,
      explanation: "Moat = competitive advantage protecting profits."
    },
    relatedTerms: ["Competitive Advantage", "Warren Buffett", "Quality", "Brand Value"]
  },
  {
    id: "recession",
    term: "Recession",
    category: "Economics",
    definition: "A recession is two consecutive quarters of negative GDP growth. It typically features rising unemployment, falling corporate profits, and declining stock markets. Recessions are normal parts of economic cycles.",
    formula: "Recession = 2+ quarters of negative GDP",
    formulaVariables: [
      { variable: "Duration", explanation: "Average 11 months" },
      { variable: "Frequency", explanation: "Roughly every 8-10 years" }
    ],
    example: {
      company: "2020 COVID Recession",
      data: "GDP fell 31% in Q2, recovered by Q3",
      calculation: "Shortest recession in history (2 months)",
      interpretation: "Markets crashed then recovered to new highs."
    },
    quiz: {
      question: "A recession is technically:",
      options: ["One bad quarter", "Two quarters of negative GDP", "A stock market crash", "High inflation"],
      correctIndex: 1,
      explanation: "Two consecutive quarters of GDP decline."
    },
    relatedTerms: ["GDP", "Bear Market", "Economic Cycle", "Depression"]
  },
  {
    id: "stagflation",
    term: "Stagflation",
    category: "Economics",
    definition: "Stagflation is the rare combination of high inflation AND high unemployment/low growth. It's difficult to solve because fighting inflation typically hurts growth. 1970s USA experienced stagflation.",
    formula: "Stagflation = High Inflation + Low Growth + High Unemployment",
    formulaVariables: [
      { variable: "Inflation", explanation: "Rising prices (bad)" },
      { variable: "Stagnation", explanation: "Slow/no growth (bad)" }
    ],
    example: {
      company: "1970s USA",
      data: "Inflation: 14%, Unemployment: 9%, Growth: 0%",
      calculation: "All indicators bad simultaneously",
      interpretation: "Central banks struggled to respond."
    },
    quiz: {
      question: "Stagflation combines:",
      options: ["Low inflation and high growth", "High inflation and low growth", "Low inflation and low growth", "High inflation and high growth"],
      correctIndex: 1,
      explanation: "Stagflation = stagnation + inflation (worst combo)."
    },
    relatedTerms: ["Inflation", "GDP", "Recession", "Fed Policy"]
  },
  {
    id: "quantitative-easing",
    term: "Quantitative Easing (QE)",
    category: "Economics",
    definition: "QE is when central banks buy bonds to inject money into the economy and lower interest rates. Used when standard rate cuts aren't enough. Can boost asset prices but risks inflation.",
    formula: "QE = Central bank buys bonds → Money supply increases",
    formulaVariables: [
      { variable: "Bond Purchases", explanation: "Trillions during crises" },
      { variable: "Interest Rates", explanation: "Pushes rates lower" }
    ],
    example: {
      company: "Fed QE (2020)",
      data: "Fed bought $4+ trillion in bonds",
      calculation: "Interest rates hit 0%, markets recovered",
      interpretation: "QE helped prevent economic collapse but boosted inflation later."
    },
    quiz: {
      question: "Quantitative Easing involves:",
      options: ["Raising taxes", "Cutting spending", "Central bank buying bonds", "Increasing interest rates"],
      correctIndex: 2,
      explanation: "QE = central bank bond purchases to stimulate economy."
    },
    relatedTerms: ["Federal Reserve", "Interest Rates", "Monetary Policy", "Inflation"]
  },
  {
    id: "fiscal-policy",
    term: "Fiscal Policy",
    category: "Economics",
    definition: "Fiscal policy is government spending and taxation decisions. Expansionary policy (more spending, less taxes) stimulates growth. Contractionary policy (less spending, more taxes) cools the economy.",
    formula: "Fiscal Stimulus = Government Spending + Tax Cuts",
    formulaVariables: [
      { variable: "Spending", explanation: "Infrastructure, transfers" },
      { variable: "Taxation", explanation: "Income, corporate, sales tax" }
    ],
    example: {
      company: "CERB (Canada 2020)",
      data: "$2,000/month to workers",
      calculation: "Massive fiscal stimulus during COVID",
      interpretation: "Prevented economic collapse but increased debt."
    },
    quiz: {
      question: "Fiscal policy is controlled by:",
      options: ["Central banks", "The government", "Stock exchanges", "Private banks"],
      correctIndex: 1,
      explanation: "Governments control spending/taxes (fiscal policy)."
    },
    relatedTerms: ["Monetary Policy", "Government Debt", "GDP", "Stimulus"]
  },
  {
    id: "monetary-policy",
    term: "Monetary Policy",
    category: "Economics",
    definition: "Monetary policy is how central banks (Bank of Canada, Federal Reserve) control money supply and interest rates. Raising rates fights inflation; lowering rates stimulates growth.",
    formula: "Rate ↑ = Slows economy/inflation. Rate ↓ = Stimulates growth.",
    formulaVariables: [
      { variable: "Policy Rate", explanation: "Overnight lending rate" },
      { variable: "Money Supply", explanation: "Controlled via QE/QT" }
    ],
    example: {
      company: "Bank of Canada (2022-23)",
      data: "Raised rates from 0.25% to 5%",
      calculation: "Fastest rate hikes in 40 years",
      interpretation: "To combat 8% inflation."
    },
    quiz: {
      question: "Monetary policy is set by:",
      options: ["The government", "Central banks", "Stock markets", "Private companies"],
      correctIndex: 1,
      explanation: "Central banks (BoC, Fed) set monetary policy."
    },
    relatedTerms: ["Interest Rates", "Federal Reserve", "Bank of Canada", "Inflation"]
  },
  {
    id: "yield-curve",
    term: "Yield Curve",
    category: "Economics",
    definition: "The yield curve plots interest rates across bond maturities. Normal: long rates > short rates. Inverted: short > long (recession signal). An inverted yield curve has predicted every recession since 1955.",
    formula: "Spread = 10-Year Yield - 2-Year Yield",
    formulaVariables: [
      { variable: "Normal", explanation: "Spread positive (healthy)" },
      { variable: "Inverted", explanation: "Spread negative (warning)" }
    ],
    example: {
      company: "2023 Inversion",
      data: "2-year: 5%, 10-year: 4%",
      calculation: "Spread = -1% (inverted)",
      interpretation: "Recession signal, but timing uncertain."
    },
    quiz: {
      question: "An inverted yield curve historically predicts:",
      options: ["Bull markets", "Recessions", "High growth", "Low inflation"],
      correctIndex: 1,
      explanation: "Inverted curves have preceded every US recession since 1955."
    },
    relatedTerms: ["Bonds", "Interest Rates", "Recession", "Treasury"]
  },
  {
    id: "sector-rotation",
    term: "Sector Rotation",
    category: "Portfolio Management",
    definition: "Sector rotation means shifting investments between sectors based on the economic cycle. Tech/growth leads early expansion; utilities/staples lead recessions. Some investors time this; others stay diversified.",
    formula: "Early cycle: Tech, Consumer. Late cycle: Energy, Materials. Recession: Utilities, Healthcare.",
    formulaVariables: [
      { variable: "Cyclical", explanation: "Tech, financials, consumer" },
      { variable: "Defensive", explanation: "Utilities, healthcare, staples" }
    ],
    example: {
      company: "Economic Cycle",
      data: "Recovery → Buy tech/growth. Slowdown → Buy utilities/healthcare.",
      calculation: "Rotate based on where we are in cycle",
      interpretation: "Hard to time perfectly. Many stay diversified."
    },
    quiz: {
      question: "During recessions, investors typically rotate to:",
      options: ["Tech stocks", "Defensive sectors", "Small caps", "Emerging markets"],
      correctIndex: 1,
      explanation: "Defensive sectors (utilities, healthcare) outperform in recessions."
    },
    relatedTerms: ["Economic Cycle", "Cyclical", "Defensive", "Diversification"]
  },
  {
    id: "tax-efficiency",
    term: "Tax Efficiency",
    category: "Tax",
    definition: "Tax efficiency means structuring investments to minimize taxes. Use TFSAs for high-growth assets, RRSPs for US dividends (no withholding), and non-registered for Canadian dividends (tax credit).",
    formula: "Optimal Location: TFSA → Growth, RRSP → US/Bonds, Non-Reg → Canadian Dividends",
    formulaVariables: [
      { variable: "Asset Location", explanation: "Which account holds what" },
      { variable: "Tax Drag", explanation: "Taxes lost to government" }
    ],
    example: {
      company: "Optimal Structure",
      data: "TFSA: VFV (US growth), RRSP: VTI (US div), Non-reg: XIU (CAD div)",
      calculation: "Minimizes tax drag across accounts",
      interpretation: "Can add 0.5%+ annually to returns."
    },
    quiz: {
      question: "Canadian dividend stocks are most tax-efficient in:",
      options: ["TFSA", "RRSP", "Non-registered accounts", "FHSA"],
      correctIndex: 2,
      explanation: "Canadian dividends get the dividend tax credit in non-registered."
    },
    relatedTerms: ["TFSA", "RRSP", "Dividend Tax Credit", "Asset Location"]
  },
  {
    id: "acb",
    term: "ACB (Adjusted Cost Base)",
    category: "Tax",
    definition: "ACB is your total investment cost including commissions and reinvested distributions. It's used to calculate capital gains. Track it carefully. CRA requires accurate ACB for tax reporting.",
    formula: "ACB = Purchase Price + Commissions + Reinvested Dividends",
    formulaVariables: [
      { variable: "Cost", explanation: "What you paid" },
      { variable: "Adjustments", explanation: "Reinvested dividends increase ACB" }
    ],
    example: {
      company: "ETF with DRIP",
      data: "Bought for $10K, $500 in reinvested dividends",
      calculation: "ACB = $10,000 + $500 = $10,500",
      interpretation: "If sold for $15K, gain = $15K - $10.5K = $4,500."
    },
    quiz: {
      question: "ACB is important for calculating:",
      options: ["Dividend yield", "Capital gains", "Interest income", "MER"],
      correctIndex: 1,
      explanation: "ACB determines your taxable capital gain/loss."
    },
    relatedTerms: ["Capital Gains", "Tax", "DRIP", "Cost Basis"]
  },
  {
    id: "withholding-tax",
    term: "Withholding Tax",
    category: "Tax",
    definition: "Withholding tax is automatically deducted from foreign dividends. US stocks have 15% withheld (recoverable in RRSP only). Holding US stocks in TFSA means losing 15% of dividends permanently.",
    formula: "US Dividend Received = Gross Dividend × (1 - 15%)",
    formulaVariables: [
      { variable: "Rate", explanation: "15% for US (Canada-US treaty)" },
      { variable: "Recovery", explanation: "RRSP: yes, TFSA: no" }
    ],
    example: {
      company: "US Stock in TFSA",
      data: "$100 dividend, 15% withheld",
      calculation: "You receive $85, lose $15 permanently",
      interpretation: "Consider holding US dividend payers in RRSP instead."
    },
    quiz: {
      question: "US dividends in a TFSA have withholding tax:",
      options: ["Recovered at tax time", "Permanently lost", "Exempt", "Doubled"],
      correctIndex: 1,
      explanation: "TFSA doesn't recover foreign withholding tax. 15% lost."
    },
    relatedTerms: ["RRSP", "TFSA", "US Dividends", "Tax Treaty"]
  },
  {
    id: "index",
    term: "Stock Index",
    category: "Basics",
    definition: "A stock index tracks a basket of stocks representing a market or sector. The S&P 500 tracks 500 large US companies. The TSX Composite tracks ~230 Canadian companies. You can't buy an index directly. Buy ETFs that track them.",
    formula: "Index Value = Weighted average of component stock prices",
    formulaVariables: [
      { variable: "Weighting", explanation: "Market cap or equal weight" },
      { variable: "Components", explanation: "Stocks in the index" }
    ],
    example: {
      company: "S&P 500",
      data: "500 largest US companies by market cap",
      calculation: "AAPL, MSFT, GOOGL are largest weights",
      interpretation: "Buy VFV or SPY to track the S&P 500."
    },
    quiz: {
      question: "You invest in an index by:",
      options: ["Buying the index directly", "Buying an ETF that tracks it", "Calling the exchange", "Buying all component stocks"],
      correctIndex: 1,
      explanation: "ETFs and index funds track indices."
    },
    relatedTerms: ["ETF", "S&P 500", "TSX", "Index Fund"]
  },
  // === ADDITIONAL CANADIAN INVESTING TERMS ===
  {
    id: "gic",
    term: "GIC (Guaranteed Investment Certificate)",
    category: "Canadian Accounts",
    definition: "A GIC is a low-risk investment where you deposit money for a fixed term (1-5 years) at a guaranteed interest rate. Principal is protected by CDIC insurance up to $100,000. Great for short-term savings goals or emergency funds when you need certainty. Interest rates are typically higher than savings accounts but lower than stock returns over time.",
    formula: "Return = Principal × Interest Rate × Term",
    formulaVariables: [
      { variable: "Principal", explanation: "Amount you deposit" },
      { variable: "Term", explanation: "Lock-in period (1-5 years)" }
    ],
    example: {
      company: "5-Year GIC at 5%",
      data: "Deposit $10,000 for 5 years",
      calculation: "$10,000 × 5% × 5 = $2,500 interest + $10,000 = $12,500",
      interpretation: "Guaranteed returns, but locked in for the term."
    },
    quiz: {
      question: "GICs are best for:",
      options: ["Long-term growth", "Guaranteed short-term returns", "Dividend income", "Currency trading"],
      correctIndex: 1,
      explanation: "GICs offer guaranteed returns but with limited growth potential."
    },
    relatedTerms: ["CDIC", "Savings Account", "Fixed Income", "Emergency Fund"]
  },
  {
    id: "hisa",
    term: "HISA (High-Interest Savings Account)",
    category: "Canadian Accounts",
    definition: "A HISA offers higher interest rates than regular savings accounts while keeping your money accessible. Perfect for emergency funds, short-term savings, or parking cash while deciding on investments. Many online banks like EQ Bank and Tangerine offer competitive HISA rates. Can be held inside TFSA or RRSP for tax advantages.",
    formula: "Interest = Balance × Annual Rate ÷ 12 (monthly)",
    formulaVariables: [
      { variable: "Balance", explanation: "Your account balance" },
      { variable: "Annual Rate", explanation: "Yearly interest rate (e.g., 4%)" }
    ],
    example: {
      company: "EQ Bank HISA",
      data: "$10,000 at 4% annual rate",
      calculation: "Monthly interest = $10,000 × 4% ÷ 12 = $33.33",
      interpretation: "Liquid savings earning better interest than big banks."
    },
    quiz: {
      question: "A HISA is best for:",
      options: ["Long-term growth", "Emergency fund", "Retirement", "Options trading"],
      correctIndex: 1,
      explanation: "HISAs keep money accessible while earning decent interest."
    },
    relatedTerms: ["Emergency Fund", "GIC", "TFSA", "EQ Bank"]
  },
  {
    id: "cad-brokers",
    term: "Canadian Discount Brokers",
    category: "Canadian Investing",
    definition: "Discount brokers like Wealthsimple, Questrade, and bank platforms let Canadians buy stocks and ETFs online. Wealthsimple Trade offers zero-commission trading for Canadian stocks and ETFs. Questrade charges low fees for US stocks. Bank brokerages are convenient but often have higher fees. Always compare commission structures before choosing.",
    formula: "Total Cost = Commission + Currency Conversion (if any) + ECN Fees",
    formulaVariables: [
      { variable: "Commission", explanation: "Per-trade fee ($0-$9.99)" },
      { variable: "Forex Fee", explanation: "Currency conversion markup (1.5-2.5%)" }
    ],
    example: {
      company: "Wealthsimple Trade",
      data: "Buy $1,000 of TD.TO stock",
      calculation: "$0 commission, no conversion needed",
      interpretation: "Zero cost to buy Canadian stocks on Wealthsimple."
    },
    quiz: {
      question: "Which broker offers $0 commission for Canadian stocks?",
      options: ["TD Direct Investing", "Wealthsimple Trade", "BMO InvestorLine", "RBC Direct"],
      correctIndex: 1,
      explanation: "Wealthsimple Trade offers commission-free Canadian trading."
    },
    relatedTerms: ["Wealthsimple", "Questrade", "Commission", "ETF"]
  },
];

// Main component continues in next part

// ==================== MAIN COMPONENT ====================
const LearnHub = ({ onBack, hideInternalBackButton = false }) => {
  // State
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [masteredTerms, setMasteredTerms] = useState(() => {
    const saved = localStorage.getItem("learnhub_mastered");
    return saved ? JSON.parse(saved) : [];
  });
  const [bookmarkedTerms, setBookmarkedTerms] = useState(() => {
    const saved = localStorage.getItem("learnhub_bookmarks");
    return saved ? JSON.parse(saved) : [];
  });
  const [recentlyViewed, setRecentlyViewed] = useState(() => {
    const saved = localStorage.getItem("learnhub_recent");
    return saved ? JSON.parse(saved) : [];
  });
  const [quizAnswer, setQuizAnswer] = useState(null);
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatUnlocked, setChatUnlocked] = useState(false);
  
  // AI Search states
  const [aiSearching, setAiSearching] = useState(false);
  const [aiSearchResult, setAiSearchResult] = useState(null);
  const [showAiResult, setShowAiResult] = useState(false);

  // Load progress from backend on mount
  useEffect(() => {
    const loadBackendProgress = async () => {
      const userData = localStorage.getItem("user_data");
      if (!userData) return;
      
      try {
        const user = JSON.parse(userData);
        const userId = user.id || user.email;
        const response = await axios.get(`${API_URL}/api/progress/${userId}`);
        const backendProgress = response.data?.learnhub_progress;
        
        if (backendProgress) {
          // Merge with localStorage (backend takes precedence)
          if (backendProgress.completed?.length > 0) {
            setMasteredTerms(prev => {
              const merged = [...new Set([...prev, ...backendProgress.completed])];
              return merged;
            });
          }
          if (backendProgress.bookmarked?.length > 0) {
            setBookmarkedTerms(prev => {
              const merged = [...new Set([...prev, ...backendProgress.bookmarked])];
              return merged;
            });
          }
          if (backendProgress.viewed?.length > 0) {
            setRecentlyViewed(prev => {
              const merged = [...new Set([...backendProgress.viewed, ...prev])].slice(0, 10);
              return merged;
            });
          }
        }
      } catch (e) {
        // Silent fail - use localStorage data
      }
    };
    
    loadBackendProgress();
  }, []);

  // Categories
  const categories = [
    "All",
    "Basics",
    "Valuation",
    "Fundamental Analysis",
    "Technical Analysis",
    "Options",
    "Portfolio Management",
    "Investment Products",
    "Canadian Accounts",
    "Market Conditions",
    "Economics",
    "Tax",
    "Trading",
    "Corporate Actions",
    "Dividends",
  ];

  // Filter terms
  const filteredTerms = useMemo(() => {
    return TERMS_DATABASE.filter((term) => {
      const matchesSearch =
        searchQuery === "" ||
        term.term.toLowerCase().includes(searchQuery.toLowerCase()) ||
        term.definition.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCategory =
        selectedCategory === "All" || term.category === selectedCategory;
      return matchesSearch && matchesCategory;
    }).sort((a, b) => a.term.localeCompare(b.term));
  }, [searchQuery, selectedCategory]);

  // Save to localStorage and backend
  useEffect(() => {
    localStorage.setItem("learnhub_mastered", JSON.stringify(masteredTerms));
    // Backend save now happens immediately in handleQuizSubmit
  }, [masteredTerms]);

  useEffect(() => {
    localStorage.setItem("learnhub_bookmarks", JSON.stringify(bookmarkedTerms));
    // Backend save now happens immediately in toggleBookmark
  }, [bookmarkedTerms]);

  useEffect(() => {
    localStorage.setItem("learnhub_recent", JSON.stringify(recentlyViewed));
  }, [recentlyViewed]);

  // AI Search function
  const handleAiSearch = async () => {
    if (!searchQuery.trim() || aiSearching) return;
    
    // Check if we have a match in the database first
    const exactMatch = TERMS_DATABASE.find(
      t => t.term.toLowerCase() === searchQuery.toLowerCase()
    );
    if (exactMatch) {
      selectTerm(exactMatch);
      return;
    }
    
    // If not found in database, use AI to explain
    setAiSearching(true);
    setShowAiResult(false);
    
    try {
      const response = await axios.post(`${API_URL}/api/learnhub/ai-search`, {
        query: searchQuery
      });
      
      setAiSearchResult(response.data);
      setShowAiResult(true);
    } catch (error) {
      toast.error("AI search unavailable. Try browsing the terms below.");
      console.error(error);
    } finally {
      setAiSearching(false);
    }
  };

  // Select a term
  const selectTerm = (term) => {
    setSelectedTerm(term);
    setQuizAnswer(null);
    setQuizSubmitted(false);
    setChatMessages([]);
    setChatUnlocked(masteredTerms.includes(term.id));
    setShowAiResult(false);

    // Add to recently viewed
    setRecentlyViewed((prev) => {
      const filtered = prev.filter((id) => id !== term.id);
      return [term.id, ...filtered].slice(0, 10);
    });
  };

  // Toggle bookmark with immediate save
  const toggleBookmark = async (termId) => {
    const isCurrentlyBookmarked = bookmarkedTerms.includes(termId);
    
    setBookmarkedTerms((prev) =>
      isCurrentlyBookmarked
        ? prev.filter((id) => id !== termId)
        : [...prev, termId]
    );
    
    // Save to backend immediately
    const userData = localStorage.getItem("user_data");
    if (userData) {
      try {
        const user = JSON.parse(userData);
        const userId = user.id || user.email;
        await axios.post(`${API_URL}/api/progress/learnhub`, {
          user_id: userId,
          article_id: termId,
          action: isCurrentlyBookmarked ? "unbookmark" : "bookmark"
        });
      } catch (e) {
        console.error("Error saving bookmark:", e);
      }
    }
  };

  // Handle quiz submission
  const handleQuizSubmit = async () => {
    if (quizAnswer === null) return;
    setQuizSubmitted(true);

    if (quizAnswer === selectedTerm.quiz.correctIndex) {
      if (!masteredTerms.includes(selectedTerm.id)) {
        setMasteredTerms((prev) => [...prev, selectedTerm.id]);
        
        // Save to backend immediately
        const userData = localStorage.getItem("user_data");
        if (userData) {
          try {
            const user = JSON.parse(userData);
            const userId = user.id || user.email;
            await axios.post(`${API_URL}/api/progress/learnhub`, {
              user_id: userId,
              article_id: selectedTerm.id,
              action: "complete"
            });
          } catch (e) {
            console.error("Error saving mastered term:", e);
          }
        }
      }
      setChatUnlocked(true);
      toast.success("Correct! Term mastered! 🎉");
    } else {
      toast.error("Incorrect. Review the explanation and try again!");
    }
  };

  // Reset quiz
  const resetQuiz = () => {
    setQuizAnswer(null);
    setQuizSubmitted(false);
  };

  // Send chat message
  const sendChatMessage = async (message) => {
    if (!message.trim() || chatLoading) return;

    const userMessage = { role: "user", content: message };
    setChatMessages((prev) => [...prev, userMessage]);
    setChatInput("");
    setChatLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/learnhub/chat`, {
        term: selectedTerm.term,
        definition: selectedTerm.definition,
        message: message,
        history: chatMessages.slice(-6),
      });

      setChatMessages((prev) => [
        ...prev,
        { role: "assistant", content: response.data.response },
      ]);
    } catch (error) {
      toast.error("Failed to get response. Please try again.");
      console.error(error);
    } finally {
      setChatLoading(false);
    }
  };

  // Progress percentage
  const progressPercent = Math.round(
    (masteredTerms.length / TERMS_DATABASE.length) * 100
  );

  // ==================== TERM DETAIL VIEW ====================
  if (selectedTerm) {
    const isCorrect = quizSubmitted && quizAnswer === selectedTerm.quiz.correctIndex;
    const isIncorrect = quizSubmitted && quizAnswer !== selectedTerm.quiz.correctIndex;

    return (
      <div className="max-w-4xl mx-auto" data-testid="learnhub-detail">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => setSelectedTerm(null)}
          className="mb-4 hover:bg-slate-100"
          data-testid="back-to-terms"
        >
          <ChevronLeft className="w-5 h-5 mr-1" /> Back to Terms
        </Button>

        {/* Term Header */}
        <Card className="mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-6 text-white">
            <div className="flex justify-between items-start">
              <div>
                <Badge className="bg-white/20 text-white mb-2">
                  {selectedTerm.category}
                </Badge>
                <h1 className="text-3xl font-bold mb-2">{selectedTerm.term}</h1>
                {masteredTerms.includes(selectedTerm.id) && (
                  <Badge className="bg-emerald-500 text-white">
                    <CheckCircle className="w-3 h-3 mr-1" /> Mastered
                  </Badge>
                )}
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => toggleBookmark(selectedTerm.id)}
                className="text-white hover:bg-white/20"
              >
                {bookmarkedTerms.includes(selectedTerm.id) ? (
                  <Star className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                ) : (
                  <StarOff className="w-6 h-6" />
                )}
              </Button>
            </div>
          </div>

          <CardContent className="p-6">
            {/* Definition */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-800 mb-2 flex items-center">
                <BookOpen className="w-5 h-5 mr-2 text-indigo-600" />
                Definition
              </h3>
              <p className="text-slate-600 leading-relaxed">
                {selectedTerm.definition}
              </p>
            </div>

            {/* Formula */}
            {selectedTerm.formula && (
              <div className="mb-6 bg-slate-50 rounded-xl p-4">
                <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center">
                  <Calculator className="w-5 h-5 mr-2 text-indigo-600" />
                  Formula
                </h3>
                <div className="bg-white rounded-lg p-4 border-2 border-indigo-100 font-mono text-lg text-center mb-4">
                  {selectedTerm.formula}
                </div>
                {selectedTerm.formulaVariables && (
                  <div className="space-y-2">
                    {selectedTerm.formulaVariables.map((v, i) => (
                      <div key={i} className="flex items-start">
                        <Badge className="bg-indigo-100 text-indigo-700 mr-2 mt-0.5">
                          {v.variable}
                        </Badge>
                        <span className="text-slate-600 text-sm">
                          {v.explanation}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Real-World Example */}
            <div className="mb-6 bg-emerald-50 rounded-xl p-4">
              <h3 className="text-lg font-semibold text-slate-800 mb-3 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-emerald-600" />
                Real-World Example
              </h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Badge className="bg-emerald-600 text-white mr-2">
                    {selectedTerm.example.company}
                  </Badge>
                </div>
                <p className="text-slate-600">
                  <strong>Data:</strong> {selectedTerm.example.data}
                </p>
                <div className="bg-white rounded-lg p-3 border border-emerald-200">
                  <p className="font-mono text-emerald-700">
                    {selectedTerm.example.calculation}
                  </p>
                </div>
                <p className="text-slate-700 italic">
                  {selectedTerm.example.interpretation}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quiz Section */}
        <Card className="mb-6">
          <CardHeader className="bg-amber-50">
            <CardTitle className="flex items-center text-amber-800">
              <Target className="w-5 h-5 mr-2" />
              Quick Quiz
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-lg font-medium text-slate-800 mb-4">
              {selectedTerm.quiz.question}
            </p>
            <div className="space-y-2 mb-4">
              {selectedTerm.quiz.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => !quizSubmitted && setQuizAnswer(index)}
                  disabled={quizSubmitted}
                  className={`w-full p-3 rounded-lg text-left transition-all ${
                    quizSubmitted
                      ? index === selectedTerm.quiz.correctIndex
                        ? "bg-emerald-100 border-2 border-emerald-500 text-emerald-800"
                        : index === quizAnswer
                        ? "bg-red-100 border-2 border-red-500 text-red-800"
                        : "bg-slate-100 text-slate-500"
                      : quizAnswer === index
                      ? "bg-indigo-100 border-2 border-indigo-500"
                      : "bg-slate-100 hover:bg-slate-200 border-2 border-transparent"
                  }`}
                  data-testid={`quiz-option-${index}`}
                >
                  <span className="font-medium mr-2">
                    {String.fromCharCode(65 + index)}.
                  </span>
                  {option}
                  {quizSubmitted && index === selectedTerm.quiz.correctIndex && (
                    <CheckCircle className="w-5 h-5 inline ml-2 text-emerald-600" />
                  )}
                  {quizSubmitted &&
                    index === quizAnswer &&
                    index !== selectedTerm.quiz.correctIndex && (
                      <XCircle className="w-5 h-5 inline ml-2 text-red-600" />
                    )}
                </button>
              ))}
            </div>

            {!quizSubmitted ? (
              <Button
                onClick={handleQuizSubmit}
                disabled={quizAnswer === null}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
                data-testid="submit-quiz"
              >
                Submit Answer
              </Button>
            ) : (
              <div>
                {isCorrect && (
                  <div className="bg-emerald-50 rounded-lg p-4 mb-4">
                    <div className="flex items-center text-emerald-700 font-semibold mb-2">
                      <Trophy className="w-5 h-5 mr-2" />
                      Correct! You&apos;ve mastered this term.
                    </div>
                    <p className="text-emerald-600 text-sm">
                      {selectedTerm.quiz.explanation}
                    </p>
                  </div>
                )}
                {isIncorrect && (
                  <div className="bg-red-50 rounded-lg p-4 mb-4">
                    <div className="flex items-center text-red-700 font-semibold mb-2">
                      <XCircle className="w-5 h-5 mr-2" />
                      Not quite right.
                    </div>
                    <p className="text-red-600 text-sm mb-3">
                      <strong>Correct Answer:</strong>{" "}
                      {selectedTerm.quiz.options[selectedTerm.quiz.correctIndex]}
                    </p>
                    <p className="text-slate-600 text-sm">
                      {selectedTerm.quiz.explanation}
                    </p>
                    <Button
                      onClick={resetQuiz}
                      variant="outline"
                      className="mt-3"
                      data-testid="try-again"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" /> Try Again
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Chat Section */}
        <Card className="mb-6">
          <CardHeader className="bg-purple-50">
            <CardTitle className="flex items-center text-purple-800">
              <MessageCircle className="w-5 h-5 mr-2" />
              Ask Questions
              {!chatUnlocked && (
                <Badge className="ml-2 bg-slate-500">Pass quiz to unlock</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {chatUnlocked ? (
              <>
                {chatMessages.length === 0 && (
                  <div className="mb-4">
                    <p className="text-sm text-slate-500 mb-2">
                      Suggested questions:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        `How do I use ${selectedTerm.term} in real investing?`,
                        `When is ${selectedTerm.term} misleading?`,
                        `Compare this for growth vs value stocks`,
                      ].map((q, i) => (
                        <Button
                          key={i}
                          variant="outline"
                          size="sm"
                          onClick={() => sendChatMessage(q)}
                          className="text-xs"
                        >
                          <Lightbulb className="w-3 h-3 mr-1" />
                          {q}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-3 max-h-80 overflow-y-auto mb-4">
                  {chatMessages.map((msg, i) => (
                    <div
                      key={i}
                      className={`p-3 rounded-lg ${
                        msg.role === "user"
                          ? "bg-indigo-100 ml-8"
                          : "bg-slate-100 mr-8"
                      }`}
                    >
                      <p className="text-sm text-slate-700 whitespace-pre-wrap">
                        {msg.content}
                      </p>
                    </div>
                  ))}
                  {chatLoading && (
                    <div className="bg-slate-100 p-3 rounded-lg mr-8">
                      <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <Input
                    value={chatInput}
                    onChange={(e) => setChatInput(e.target.value)}
                    placeholder="Ask a question about this term..."
                    onKeyPress={(e) =>
                      e.key === "Enter" && sendChatMessage(chatInput)
                    }
                    className="flex-1"
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={() => sendChatMessage(chatInput)}
                    disabled={chatLoading || !chatInput.trim()}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="send-chat"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <GraduationCap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Pass the quiz above to unlock AI-powered Q&A!</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Related Terms */}
        {selectedTerm.relatedTerms && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Related Terms</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {selectedTerm.relatedTerms.map((related, i) => {
                  const relatedTerm = TERMS_DATABASE.find(
                    (t) =>
                      t.term.toLowerCase().includes(related.toLowerCase()) ||
                      related.toLowerCase().includes(t.term.toLowerCase())
                  );
                  return (
                    <Button
                      key={i}
                      variant="outline"
                      size="sm"
                      onClick={() => relatedTerm && selectTerm(relatedTerm)}
                      disabled={!relatedTerm}
                      className="text-sm"
                    >
                      {related}
                      {relatedTerm && masteredTerms.includes(relatedTerm.id) && (
                        <CheckCircle className="w-3 h-3 ml-1 text-emerald-500" />
                      )}
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ==================== MAIN LIST VIEW ====================
  return (
    <div className="max-w-6xl mx-auto" data-testid="learnhub-main">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-2xl p-8 text-white mb-8">
        <div className="flex items-center gap-4 mb-4">
          {!hideInternalBackButton && (
            <Button
              variant="ghost"
              onClick={onBack}
              className="text-white hover:bg-white/20 -ml-2"
              data-testid="back-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
          )}
          <div>
            <h1 className="text-4xl font-bold">Learn Hub</h1>
            <p className="text-indigo-100">
              Master {TERMS_DATABASE.length}+ investing terms with AI-powered search
            </p>
          </div>
        </div>

        {/* AI Search Bar */}
        <div className="relative mb-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search any investing term... (AI-powered)"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowAiResult(false);
                }}
                onKeyPress={(e) => e.key === "Enter" && handleAiSearch()}
                className="w-full pl-12 pr-4 py-4 rounded-xl text-slate-800 text-lg"
                data-testid="search-input"
              />
            </div>
            <Button
              onClick={handleAiSearch}
              disabled={aiSearching || !searchQuery.trim()}
              className="bg-white text-indigo-600 hover:bg-indigo-50 px-6"
            >
              {aiSearching ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  AI Search
                </>
              )}
            </Button>
          </div>
          {filteredTerms.length > 0 && searchQuery && (
            <Badge className="absolute left-4 -bottom-6 bg-white/20">
              {filteredTerms.length} matching terms
            </Badge>
          )}
        </div>

        {/* Progress */}
        <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-sm text-indigo-100">Learning Progress</p>
              <p className="text-2xl font-bold">
                {masteredTerms.length} / {TERMS_DATABASE.length} Terms Mastered
              </p>
            </div>
            <div className="text-4xl font-bold">{progressPercent}%</div>
          </div>
          <Progress value={progressPercent} className="h-3 bg-white/30" />
        </div>
      </div>

      {/* AI Search Result */}
      {showAiResult && aiSearchResult && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <Card className="border-2 border-purple-200 bg-purple-50">
            <CardHeader>
              <CardTitle className="flex items-center text-purple-800">
                <Sparkles className="w-5 h-5 mr-2" />
                AI Explanation: {searchQuery}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="prose prose-slate max-w-none">
                <p className="text-slate-700 whitespace-pre-wrap">
                  {aiSearchResult.explanation}
                </p>
              </div>
              {aiSearchResult.relatedTerms?.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm text-slate-600 mb-2">Related terms in our database:</p>
                  <div className="flex flex-wrap gap-2">
                    {aiSearchResult.relatedTerms.map((termId, i) => {
                      const term = TERMS_DATABASE.find(t => t.id === termId);
                      return term ? (
                        <Button
                          key={i}
                          variant="outline"
                          size="sm"
                          onClick={() => selectTerm(term)}
                        >
                          {term.term}
                        </Button>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAiResult(false)}
                className="mt-4 text-slate-500"
              >
                Close AI result
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Category Filters */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
          Filter by Category
        </h3>
        <div className="flex flex-wrap gap-2">
          {categories.map((cat) => (
            <Button
              key={cat}
              variant={selectedCategory === cat ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(cat)}
              className={
                selectedCategory === cat
                  ? "bg-indigo-600 text-white"
                  : "bg-white hover:bg-slate-100"
              }
              data-testid={`category-${cat.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {cat}
              {cat !== "All" && (
                <span className="ml-1 text-xs opacity-70">
                  (
                  {
                    TERMS_DATABASE.filter((t) => t.category === cat).length
                  }
                  )
                </span>
              )}
            </Button>
          ))}
        </div>
      </div>

      {/* Quick Access */}
      {(bookmarkedTerms.length > 0 || recentlyViewed.length > 0) && (
        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {bookmarkedTerms.length > 0 && (
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center">
                  <Star className="w-4 h-4 mr-2 text-yellow-500" />
                  Bookmarked ({bookmarkedTerms.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2">
                <div className="flex flex-wrap gap-1">
                  {bookmarkedTerms.slice(0, 5).map((id) => {
                    const term = TERMS_DATABASE.find((t) => t.id === id);
                    return term ? (
                      <Button
                        key={id}
                        variant="ghost"
                        size="sm"
                        onClick={() => selectTerm(term)}
                        className="text-xs"
                      >
                        {term.term}
                      </Button>
                    ) : null;
                  })}
                </div>
              </CardContent>
            </Card>
          )}
          {recentlyViewed.length > 0 && (
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-slate-500" />
                  Recently Viewed
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2">
                <div className="flex flex-wrap gap-1">
                  {recentlyViewed.slice(0, 5).map((id) => {
                    const term = TERMS_DATABASE.find((t) => t.id === id);
                    return term ? (
                      <Button
                        key={id}
                        variant="ghost"
                        size="sm"
                        onClick={() => selectTerm(term)}
                        className="text-xs"
                      >
                        {term.term}
                      </Button>
                    ) : null;
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Terms Grid */}
      <div>
        <h3 className="text-xl font-bold text-slate-900 mb-4">
          {selectedCategory === "All" ? "All Terms" : selectedCategory} (
          {filteredTerms.length})
        </h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTerms.map((term) => (
            <motion.button
              key={term.id}
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => selectTerm(term)}
              className="bg-white p-5 rounded-xl text-left shadow-sm border border-slate-200 hover:border-indigo-300 hover:shadow-md transition-all relative group"
              data-testid={`term-${term.id}`}
            >
              {masteredTerms.includes(term.id) && (
                <div className="absolute top-3 right-3">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                </div>
              )}
              {bookmarkedTerms.includes(term.id) && (
                <div className="absolute top-3 right-10">
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                </div>
              )}
              <h4 className="font-bold text-lg text-slate-900 group-hover:text-indigo-600 transition-colors mb-1 pr-12">
                {term.term}
              </h4>
              <p className="text-xs text-indigo-600 font-medium mb-2">
                {term.category}
              </p>
              <p className="text-sm text-slate-600 line-clamp-2">
                {term.definition}
              </p>
              <div className="mt-3 flex items-center text-indigo-600 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                Learn more <ArrowRight className="w-4 h-4 ml-1" />
              </div>
            </motion.button>
          ))}
        </div>

        {filteredTerms.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Search className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p className="mb-4">No terms found matching &quot;{searchQuery}&quot;</p>
            <Button
              onClick={handleAiSearch}
              disabled={aiSearching}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {aiSearching ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 mr-2" />
              )}
              Ask AI to explain &quot;{searchQuery}&quot;
            </Button>
            <Button
              variant="link"
              onClick={() => {
                setSearchQuery("");
                setSelectedCategory("All");
              }}
              className="ml-2"
            >
              Clear filters
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearnHub;
